/**
 * Reluxe Custom Filter Sidebar - ALGOLIA VERSION
 * Uses Algolia for instant search (<50ms)
 * Replaces the custom API with Algolia while keeping the same UI
 */

(function() {
  'use strict';

  // Algolia Configuration
  const ALGOLIA_APP_ID = '18OR3RE3S6';
  const ALGOLIA_SEARCH_KEY = 'f990a8cbd4fd415d210a6f3c4dcd060f';
  const ALGOLIA_INDEX_NAME = 'products';

  // API for favorites/likes (still use our server)
  const API_BASE = 'https://api.thereluxe.com';
  const STORE_URL = 'https://thereluxe.com';

  // Algolia client (initialized later)
  let searchClient = null;
  let searchIndex = null;

  // Algolia Insights - event tracking for analytics, recommendations, re-ranking
  let currentQueryID = null;  // Stores queryID from latest search response
  let currentHits = [];       // Stores current search result objectIDs for position tracking
  let insightsUserToken = null;  // Anonymous user token for event tracking

  // Truncate title to max words (no ellipsis)
  function truncateTitle(title, maxWords = 10) {
    if (!title) return '';
    const words = title.split(/\s+/);
    if (words.length <= maxWords) return title;
    return words.slice(0, maxWords).join(' ');
  }

  // Strip brand name from title for display (brand is shown separately)
  // Only strips if brand is at START of title followed by a space (preserves model names like "Dioriviera")
  function stripBrandFromTitle(title, brand) {
    if (!title || !brand) return title || '';

    // Normalize function: lowercase, remove periods, collapse multiple spaces
    const normalize = (str) => str.toLowerCase().replace(/\./g, '').replace(/\s+/g, ' ').trim();

    const normalizedTitle = normalize(title);
    const normalizedBrand = normalize(brand);

    // Check if title starts with "brand " (brand followed by space)
    // This prevents stripping "Dior" from "Dioriviera" or "ABCDior"
    const expectedPrefix = normalizedBrand + ' ';
    if (normalizedTitle.startsWith(expectedPrefix)) {
      // Find where to cut in the original title (accounting for periods/spacing differences)
      let titleIndex = 0;
      let brandIndex = 0;

      while (brandIndex < normalizedBrand.length && titleIndex < title.length) {
        const titleChar = title[titleIndex].toLowerCase();
        const brandChar = normalizedBrand[brandIndex];

        // Skip periods in original title
        if (titleChar === '.') {
          titleIndex++;
          continue;
        }

        if (titleChar === brandChar) {
          titleIndex++;
          brandIndex++;
        } else {
          break;
        }
      }

      // Skip any trailing periods/spaces left after brand match (e.g. "Tiffany & Co.")
      while (titleIndex < title.length && (title[titleIndex] === '.' || title[titleIndex] === ' ')) {
        titleIndex++;
      }

      return title.substring(titleIndex).trim();
    }
    return title;
  }

  // Format an amount in the given currency for display — always no decimals
  function formatAmount(amount, currency) {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount);
    } catch (e) {
      return '$' + Math.round(amount).toLocaleString();
    }
  }

  // Format a USD price using the active currency rate (used for initial render before exact prices load)
  function formatPrice(usdPrice) {
    const rate = parseFloat(window.Shopify?.currency?.rate || 1);
    const active = window.Shopify?.currency?.active || 'USD';
    return formatAmount((usdPrice || 0) * rate, active);
  }

  // Format Shopify's raw cents (from /products/handle.js) in the active currency.
  // Shopify always returns prices ×100 regardless of currency, so divide by 100.
  function formatShopifyPrice(shopifyCents) {
    const active = window.Shopify?.currency?.active || 'USD';
    return formatAmount(shopifyCents / 100, active);
  }

  // State - all filters now support multi-select (arrays)
  let currentFilters = {
    collection: 'all',
    brands: [],
    categories: [],
    product_types: [],
    colors: [],
    conditions: [],
    materials: [],
    sizes: [],
    size_system: 'us',  // 'us' or 'eu' — toggles which size facet to show
    size_group_open: false,  // Persist Size group expand state across re-renders
    department: '',
    price_ranges: [],  // Array of {min, max} objects for multi-select price
    custom_min_price: null,  // Custom min price input
    custom_max_price: null,  // Custom max price input
    page: 0,  // Algolia uses 0-indexed pages
    per_page: 100,
    sort: 'newest',
    search: ''
  };

  // Store the initial collection-based filters (from URL)
  let collectionFilters = {
    brand: null,
    category: null,
    categories: null,  // Array for multi-category collections (e.g., apparel)
    product_type: null,
    product_types: null,  // Array for multi-type collections (e.g., cosmetic-cases)
    department: null,
    saleTag: false,
    vdayTag: false,  // Valentine's Day 2026 collection
    editorsPickTag: false,  // Editor's Picks curated collection
    prettyInPinkTag: false,  // Pretty in Pink themed collection
    mothersDayTag: false,  // Mother's Day 2026 collection
    iconsTag: false,  // Icons curated collection
    newArrivals: false,  // Filter by recent products
    minTimestamp: null,  // Unix timestamp for new arrivals cutoff
    search: null,  // Search query from collection (uses OR logic for words)
    titleRequired: null,  // Word that must appear in title (e.g., "belt" for belts collection)
    restrictToTitle: false  // Only search in title field (for brand+title search collections)
  };

  let filterData = null;
  let isLoading = false;
  let facetData = {};  // Store facet counts from Algolia
  let initialFacets = null;  // Cache initial facets for multi-select

  // Customer favorites
  let customerFavorites = [];
  let favoritesLoaded = false;
  let reluxeUserId = null;
  let likesCache = {};
  let variantCache = {};

  // Generate or retrieve anonymous user token for Algolia Insights
  function getInsightsUserToken() {
    if (insightsUserToken) return insightsUserToken;
    // Check for existing token in localStorage
    try {
      insightsUserToken = localStorage.getItem('_algolia_ut');
      if (!insightsUserToken) {
        // Generate a random anonymous token
        insightsUserToken = 'anon-' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
        localStorage.setItem('_algolia_ut', insightsUserToken);
      }
    } catch (e) {
      // Fallback if localStorage unavailable
      insightsUserToken = 'anon-' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
    }
    return insightsUserToken;
  }

  // Send event to Algolia Insights API
  function sendAlgoliaEvent(eventType, eventPayload) {
    const url = `https://insights.algolia.io/1/events`;
    const payload = {
      events: [{
        userToken: getInsightsUserToken(),
        ...eventPayload
      }]
    };
    const body = JSON.stringify(payload);
    // keepalive: true ensures the request completes even if user navigates away
    fetch(url, {
      method: 'POST',
      headers: {
        'X-Algolia-Application-Id': ALGOLIA_APP_ID,
        'X-Algolia-API-Key': ALGOLIA_SEARCH_KEY,
        'Content-Type': 'application/json'
      },
      body,
      keepalive: true
    }).catch(() => {});
  }

  // Initialize Algolia client
  function initAlgolia() {
    if (typeof algoliasearch === 'undefined') {
      console.error('[ReluxeFilters] Algolia library not loaded!');
      return false;
    }
    searchClient = algoliasearch(ALGOLIA_APP_ID, ALGOLIA_SEARCH_KEY);
    searchIndex = searchClient.initIndex(ALGOLIA_INDEX_NAME);
    // Initialize user token early
    getInsightsUserToken();
    console.log('[ReluxeFilters] Algolia initialized with Insights tracking');
    return true;
  }

  // Optimize Cloudinary URLs for smaller file sizes
  function optimizeImageUrl(url, width = 400) {
    if (!url) return url;
    if (url.includes('cloudinary.com')) {
      return url.replace('/c_pad,', `/c_pad,w_${width},q_auto,f_auto,`).replace('/c_pad%2C', `/c_pad%2Cw_${width}%2Cq_auto%2Cf_auto%2C`);
    }
    return url;
  }

  // Detect current collection from URL - SMART PARSING
  // Automatically extracts brand, category, product type, and uses remaining words as search
  function detectCollection() {
    const path = window.location.pathname;

    // Check if this is a search page
    if (path === '/search' || path.startsWith('/search')) {
      const urlParams = new URLSearchParams(window.location.search);
      const searchQuery = urlParams.get('q') || '';
      return { collection: 'all', brand: null, category: null, product_type: null, search: searchQuery, isSearch: true };
    }

    // Department + Category collections (must check BEFORE general department collections)
    if (path.includes('/collections/women-bags')) return { collection: 'all', department: 'Women', category: 'Bags' };
    if (path.includes('/collections/women-apparel') || path.includes('/collections/women-clothing')) return { collection: 'all', department: 'Women', category: 'Apparel' };
    if (path.includes('/collections/women-jewelry')) return { collection: 'all', department: 'Women', category: 'Jewelry' };
    if (path.includes('/collections/women-shoes')) return { collection: 'all', department: 'Women', category: 'Shoes' };
    if (path.includes('/collections/men-bags')) return { collection: 'all', department: 'Men', category: 'Bags' };
    if (path.includes('/collections/men-apparel') || path.includes('/collections/men-clothing')) return { collection: 'all', department: 'Men', category: 'Apparel' };
    if (path.includes('/collections/men-jewelry')) return { collection: 'all', department: 'Men', category: 'Jewelry' };
    if (path.includes('/collections/men-shoes')) return { collection: 'all', department: 'Men', category: 'Shoes' };

    // Main department collections (English + Spanish) - general department only
    if (path.includes('/collections/women') || path.includes('/collections/mujeres')) return { collection: 'all', department: 'Women' };
    if (path.includes('/collections/men') || path.includes('/collections/hombres')) return { collection: 'all', department: 'Men' };

    // New arrivals - filter to last 14 days (English + Spanish: nuevo, novedades, recien-llegados)
    if (path.includes('/collections/new-arrivals') || path.includes('/collections/new') ||
        path.includes('/collections/nuevo') || path.includes('/collections/novedades') ||
        path.includes('/collections/recien-llegados')) {
      const fourteenDaysAgo = Math.floor(Date.now() / 1000) - (14 * 24 * 60 * 60);
      return { collection: 'all', brand: null, category: null, product_type: null, newArrivals: true, minTimestamp: fourteenDaysAgo };
    }

    // Sale collection - filter by "Sale" tag (English + Spanish: rebajas, oferta, ofertas)
    if (path.includes('/collections/sale') || path.includes('/collections/rebajas') ||
        path.includes('/collections/oferta') || path.includes('/collections/ofertas')) {
      return { collection: 'all', brand: null, category: null, product_type: null, saleTag: true };
    }

    // Valentine's Day 2026 collection - filter by "vday" tag
    if (path.includes('/collections/valentines-day-2026') || path.includes('/collections/san-valentin-2026')) {
      return { collection: 'all', brand: null, category: null, product_type: null, vdayTag: true };
    }

    // Mother's Day 2026 collection - filter by "mday26" tag
    if (path.includes('/collections/mothers-day-2026') || path.includes('/collections/dia-de-la-madre-2026')) {
      return { collection: 'all', brand: null, category: null, product_type: null, mothersDayTag: true };
    }

    // Editor's Picks - curated collection, filter by "editors-pick" tag
    if (path.includes('/collections/editors-picks')) {
      return { collection: 'all', brand: null, category: null, product_type: null, editorsPickTag: true };
    }

    // Icons - curated collection, filter by "icons" tag
    if (path.includes('/collections/icons')) {
      return { collection: 'all', brand: null, category: null, product_type: null, iconsTag: true };
    }

    // Pretty in Pink - filter by "Pink" tag, excluding pink-gold watches
    if (path.includes('/collections/pretty-in-pink')) {
      return { collection: 'all', brand: null, category: null, product_type: null, prettyInPinkTag: true };
    }

    // All products collection - show everything without filters (English + Spanish)
    if (path.includes('/collections/all-products') || path.includes('/collections/all') ||
        path.includes('/collections/todos-los-productos') || path.includes('/collections/todos')) {
      return { collection: 'all', brand: null, category: null, product_type: null };
    }

    // Brand + Category combinations (for brand-specific category pages)
    const brandCategoryCollections = {
      'tiffany-bags': {
        brand: 'Tiffany & Co.',
        category: 'Bags'
      }
    };

    // Brand + Title Search combinations (for collections where product_type data is unreliable)
    // These filter by brand AND search for specific words in title
    const brandTitleSearchCollections = {
      // Empty - tiffany-bags now uses brandCategoryCollections
    };

    // Parse collection slug early for special collection checks
    const earlyMatch = path.match(/\/collections\/([^\/]+)/);
    if (earlyMatch) {
      const earlySlug = earlyMatch[1].toLowerCase();
      // Check brandCategoryCollections first
      if (brandCategoryCollections[earlySlug]) {
        const config = brandCategoryCollections[earlySlug];
        console.log('[ReluxeFilters] Brand+Category collection:', earlySlug, config);
        return {
          collection: 'all',
          brand: config.brand,
          category: config.category
        };
      }
      // Then check brandTitleSearchCollections
      if (brandTitleSearchCollections[earlySlug]) {
        const config = brandTitleSearchCollections[earlySlug];
        console.log('[ReluxeFilters] Brand+TitleSearch collection:', earlySlug, config);
        return {
          collection: 'all',
          brand: config.brand,
          search: config.search,
          restrictToTitle: config.restrictToTitle || false
        };
      }
    }

    // Multi-category collections - currently empty since Apparel uses single category
    // Keep this structure for future use if needed
    const multiCategoryCollections = {
      // Empty - apparel now uses single category via categoryKeywords
    };

    // Collections that need multiple product types (no search query) - English + Spanish
    // NOTE: shoes, home, watches now use single category via categoryKeywords (hides category filter in sidebar)
    const multiProductTypeCollections = {
      'cosmetic-cases': ['Vanity Bag', 'Pouch'],
      'cosmetic-bags': ['Vanity Bag', 'Pouch'],
      // Clutch/Wallets - include both clutches and wallets (singular and plural forms)
      'clutch-wallets': ['Wallets', 'Clutch', 'Card Holders', 'Coin Purse', 'Key Holder'],
      'clutches-wallets': ['Wallets', 'Clutch', 'Card Holders', 'Coin Purse', 'Key Holder'],
      'wallets': ['Wallets', 'Card Holders', 'Coin Purse', 'Key Holder'],
      // Scarves and Twillies
      'scarves-twillies': ['Scarves'],
      'twillies': ['Scarves'],
      // Charms - includes bag charms, keychains with charms, etc.
      'charms': ['Keychain & Charms'],
      // Spanish
      'estuches-cosmeticos': ['Vanity Bag', 'Pouch'],
      'bolsas-cosmeticas': ['Vanity Bag', 'Pouch'],
      'bufandas': ['Scarves']
    };

    // Collections that require a specific word in the title (for more strict filtering)
    const titleRequiredCollections = {
      // Note: belts now uses productTypeKeywords ('belts': 'Belt')
      // Note: tiffany-bags now uses brandCategoryCollections (brand + category)
    };

    // Brand slugs (match database values exactly) - ordered by slug length (longest first for accurate matching)
    const brandMap = {
      'christian-louboutin': 'Christian Louboutin',
      'alexander-wang': 'Alexander Wang',
      'bottega-veneta': 'Bottega Veneta',
      'christian-dior': 'Dior',
      'dolce-gabbana': 'Dolce & Gabbana',
      'louis-vuitton': 'Louis Vuitton',
      'saint-laurent': 'Saint Laurent',
      'balenciaga': 'Balenciaga',
      'burberry': 'Burberry',
      'louboutin': 'Christian Louboutin',
      'van-cleef-arpels': 'Van Cleef & Arpels',
      'van-cleef': 'Van Cleef & Arpels',
      'valentino': 'Valentino',
      'givenchy': 'Givenchy',
      'versace': 'Versace',
      'tiffany': 'Tiffany & Co.',
      'miu-miu': 'Miu Miu',
      'bulgari': 'Bulgari',
      'bvlgari': 'Bvlgari',
      'cartier': 'Cartier',
      'chanel': 'Chanel',
      'celine': 'Celine',
      'goyard': 'Goyard',
      'hermes': 'Hermes',
      'coach': 'Coach',
      'fendi': 'Fendi',
      'gucci': 'Gucci',
      'loewe': 'Loewe',
      'prada': 'Prada',
      'rolex': 'Rolex',
      'dior': 'Dior',
      'mcm': 'MCM',
      'ysl': 'Saint Laurent',
      'chrome-hearts': 'Chrome Hearts',
      'moncler': 'Moncler',
      'harry-winston': 'Harry Winston',
      'max-mara': 'Max Mara',
      'brunello-cucinelli': 'Brunello Cucinelli'
    };

    // Category keywords (match database values) - English + Spanish
    const categoryKeywords = {
      // English
      'small-leather-goods': 'Wallets',
      'accessories': 'Accessories',
      'outerwear': 'Jackets & Coats',
      'jewellery': 'Jewelry',
      'clothing': 'Clothing',
      'footwear': 'Shoes',
      'knitwear': 'Sweaters',
      'homeware': 'Home',
      'trousers': 'Pants',
      'jackets': 'Jackets & Coats',
      'jewelry': 'Jewelry',
      'sweaters': 'Sweaters',
      'watches': 'Watches',
      'apparel': 'Apparel',
      'dresses': 'Dresses',
      'scarves': 'Scarves',
      'wallets': 'Wallets',
      'shorts': 'Shorts',
      'shirts': 'Shirts',
      'skirts': 'Skirts',
      'coats': 'Jackets & Coats',
      'belts': 'Belts',
      'shoes': 'Shoes',
      'pants': 'Pants',
      'jeans': 'Jeans',
      'bags': 'Bags',
      'home': 'Home',
      'home-decor': 'Home',
      'slg': 'Wallets',
      // Spanish translations
      'accesorios': 'Accessories',
      'joyeria': 'Jewelry',
      'joyería': 'Jewelry',
      'ropa': 'Clothing',
      'calzado': 'Shoes',
      'zapatos': 'Shoes',
      'relojes': 'Watches',
      'vestidos': 'Dresses',
      'bufandas': 'Scarves',
      'carteras': 'Wallets',
      'billeteras': 'Wallets',
      'pantalones-cortos': 'Shorts',
      'camisas': 'Shirts',
      'faldas': 'Skirts',
      'abrigos': 'Jackets & Coats',
      'chaquetas': 'Jackets & Coats',
      'cinturones': 'Belts',
      'pantalones': 'Pants',
      'vaqueros': 'Jeans',
      'bolsos': 'Bags',
      'bolsas': 'Bags',
      'hogar': 'Home',
      'sueteres': 'Sweaters',
      'jerseis': 'Sweaters'
    };

    // Product type keywords (match database values EXACTLY) - these are specific item types
    const productTypeKeywords = {
      // Multi-word product types (check these first)
      'hair-accessories': 'Hair Accessories',
      'hair-accessory': 'Hair Accessories',
      'luggage-tags': 'Luggage Tag',
      'luggage-tag': 'Luggage Tag',
      'phone-cases': 'Phone Case',
      'phone-case': 'Phone Case',
      'card-holders': 'Card Holders',
      'card-holder': 'Card Holders',
      'card-cases': 'Card Holders',
      'card-case': 'Card Holders',
      'key-holders': 'Key Holder',
      'key-holder': 'Key Holder',
      'coin-purses': 'Coin Purse',
      'coin-purse': 'Coin Purse',
      'crossbody-bags': 'Crossbody',
      'shoulder-bags': 'Shoulder Bag',
      'bucket-bags': 'Bucket Bag',
      'messenger-bags': 'Messenger Bags',
      'travel-bags': 'Travel Bags',
      'duffle-bags': 'Travel Bags',
      'vanity-bags': 'Vanity Bag',
      'hobo-bags': 'Hobo',
      'tote-bags': 'Totes',
      'belt-bags': 'Belt Bag',
      'belt-bag': 'Belt Bag',
      'bag-straps': 'Bag Straps',
      'bag-strap': 'Bag Straps',
      't-shirts': 'T-Shirt',
      't-shirt': 'T-Shirt',
      // Single word product types
      'sweatshirts': 'Sweatshirt',
      'sweatshirt': 'Sweatshirt',
      'espadrilles': 'Espadrilles',
      'eyeglasses': 'Eyewear',
      'sunglasses': 'Eyewear',
      'briefcases': 'Business Bags',
      'briefcase': 'Business Bags',
      'cardigans': 'Cardigan',
      'cardigan': 'Cardigan',
      'cufflinks': 'Cufflinks',
      'tableware': 'Tableware',
      'drinkware': 'Drinkware',
      'ashtrays': 'Ashtray',
      'ashtray': 'Ashtray',
      'towels': 'Towel',
      'towel': 'Towel',
      'vases': 'Vase',
      'vase': 'Vase',
      'toys': 'Toy',
      'toy': 'Toy',
      'platforms': 'Platforms',
      'platform': 'Platforms',
      'bracelets': 'Bracelet',
      'bracelet': 'Bracelet',
      'necklaces': 'Necklace',
      'necklace': 'Necklace',
      'backpacks': 'Backpack',
      'backpack': 'Backpack',
      'messenger': 'Messenger Bags',
      'crossbody': 'Crossbody',
      'slippers': 'Slippers',
      'sneakers': 'Sneakers',
      'brooches': 'Brooch',
      'earrings': 'Earrings',
      'earring': 'Earrings',
      'pendants': 'Pendant',
      'pendant': 'Pendant',
      'clutches': 'Clutch',
      'shoulder': 'Shoulder Bag',
      'sweaters': 'Sweater',
      'sweater': 'Sweater',
      'hoodies': 'Hoodie',
      'hoodie': 'Hoodie',
      'blazers': 'Jacket',
      'blazer': 'Jacket',
      'sandals': 'Sandals',
      'loafers': 'Loafers',
      'pouches': 'Pouch',
      'watches': 'Watch',
      'wallets': 'Wallets',
      'brooch': 'Brooch',
      'agendas': 'Agendas',
      'agenda': 'Agendas',
      'handbags': 'Handbag',
      'handbag': 'Handbag',
      'scarves': 'Scarves',
      'dresses': 'Dress',
      'jackets': 'Jacket',
      'blouses': 'Blouse',
      'oxfords': 'Oxfords',
      'bucket': 'Bucket Bag',
      'duffle': 'Travel Bags',
      'vanity': 'Vanity Bag',
      'charms': 'Keychain & Charms',
      'clutch': 'Clutch',
      'totes': 'Totes',
      'tote': 'Totes',
      'pouch': 'Pouch',
      'watch': 'Watch',
      'wallet': 'Wallets',
      'scarf': 'Scarves',
      'gloves': 'Gloves',
      'boots': 'Boots',
      'heels': 'Heels',
      'flats': 'Flats',
      'mules': 'Mules',
      'rings': 'Ring',
      'charm': 'Keychain & Charms',
      'belts': 'Belts',
      'skirts': 'Skirt',
      'shorts': 'Shorts',
      'shirts': 'Shirt',
      'suits': 'Suit',
      'vests': 'Vest',
      'polos': 'Polo',
      'pants': 'Pants',
      'jeans': 'Jeans',
      'coats': 'Coat',
      'hobo': 'Hobo',
      'hats': 'Hats',
      'ties': 'Ties',
      'ring': 'Ring',
      'belt': 'Belts',
      'skirt': 'Skirt',
      'shirt': 'Shirt',
      'blouse': 'Blouse',
      'dress': 'Dress',
      'jacket': 'Jacket',
      'coat': 'Coat',
      'suit': 'Suit',
      'vest': 'Vest',
      'polo': 'Polo',
      'tops': 'Top',
      'top': 'Top',
      'hat': 'Hats',
      'tie': 'Ties',
      // Spanish translations
      'accesorios-cabello': 'Hair Accessories',
      'etiquetas-equipaje': 'Luggage Tag',
      'fundas-telefono': 'Phone Case',
      'tarjeteros': 'Card Holders',
      'porta-tarjetas': 'Card Holders',
      'llaveros': 'Key Holder',
      'monederos': 'Coin Purse',
      'decoracion-hogar': 'Home Decor',
      'bolsos-tote': 'Totes',
      'bolsos-cruzados': 'Crossbody',
      'bolsos-bandolera': 'Crossbody',
      'bolsos-hombro': 'Shoulder Bag',
      'bolsos-cubo': 'Bucket Bag',
      'bolsos-mensajero': 'Messenger Bags',
      'bolsas-deporte': 'Travel Bags',
      'neceseres': 'Vanity Bag',
      'camisetas': 'T-Shirt',
      'sudaderas': 'Sweatshirt',
      'alpargatas': 'Espadrilles',
      'gafas-de-sol': 'Eyewear',
      'anteojos': 'Eyewear',
      'gafas': 'Eyewear',
      'maletines': 'Business Bags',
      'cardiganes': 'Cardigan',
      'gemelos': 'Cufflinks',
      'pulseras': 'Bracelet',
      'collares': 'Necklace',
      'mochilas': 'Backpack',
      'pantuflas': 'Slippers',
      'zapatillas': 'Sneakers',
      'tenis': 'Sneakers',
      'broches': 'Brooch',
      'aretes': 'Earrings',
      'pendientes': 'Earrings',
      'colgantes': 'Pendant',
      'clutches': 'Clutch',
      'bolsos-clutch': 'Clutch',
      'sueteres': 'Sweater',
      'jerseis': 'Sweater',
      'sudaderas-capucha': 'Hoodie',
      'sandalias': 'Sandals',
      'mocasines': 'Loafers',
      'bolsitas': 'Pouch',
      'relojes': 'Watch',
      'billeteras': 'Wallets',
      'carteras': 'Wallets',
      'gargantillas': 'Necklace',
      'agendas': 'Agendas',
      'bolsos-mano': 'Handbag',
      'bufandas': 'Scarves',
      'vestidos': 'Dress',
      'chaquetas': 'Jacket',
      'blusas': 'Blouse',
      'dijes': 'Keychain & Charms',
      'guantes': 'Gloves',
      'botas': 'Boots',
      'tacones': 'Heels',
      'bailarinas': 'Flats',
      'mulas': 'Mules',
      'anillos': 'Ring',
      'cinturones': 'Belts',
      'faldas': 'Skirt',
      'camisas': 'Shirt',
      'trajes': 'Suit',
      'chalecos': 'Vest',
      'corbatas': 'Ties',
      'sombreros': 'Hats',
      'gorras': 'Hats',
      'abrigos': 'Coat'
    };

    // Parse collection slug
    const match = path.match(/\/collections\/([^\/]+)/);
    if (!match) {
      return { collection: 'all', brand: null, category: null };
    }

    const collectionSlug = match[1].toLowerCase();

    // Check multi-product-type collections FIRST (e.g., cosmetic-cases = Vanity Bag + Pouch)
    if (multiProductTypeCollections[collectionSlug]) {
      return {
        collection: 'all',
        product_types: multiProductTypeCollections[collectionSlug]
      };
    }

    // Check title-required collections (e.g., belts must have "belt" in title)
    if (titleRequiredCollections[collectionSlug]) {
      return {
        collection: 'all',
        titleRequired: titleRequiredCollections[collectionSlug]
      };
    }

    // Check multi-category collections
    if (multiCategoryCollections[collectionSlug]) {
      return {
        collection: 'all',
        categories: multiCategoryCollections[collectionSlug]
      };
    }

    // SMART PARSING: Extract brand, category, product type, and remaining words as search
    let detectedBrand = null;
    let detectedCategory = null;
    let detectedProductType = null;
    let remainingSlug = collectionSlug;

    // Step 1: Extract brand (check longest matches first)
    for (const [slug, brand] of Object.entries(brandMap)) {
      if (remainingSlug.includes(slug)) {
        detectedBrand = brand;
        // Remove brand from slug (including surrounding hyphens)
        remainingSlug = remainingSlug.replace(slug, '').replace(/^-+|-+$/g, '').replace(/-{2,}/g, '-');
        break;
      }
    }

    // Step 1.5: Check if remaining slug matches multi-category collection (e.g., "apparel" after brand extracted)
    // This handles brand + multi-category URLs like "bottega-veneta-apparel"
    if (multiCategoryCollections[remainingSlug]) {
      console.log('[ReluxeFilters] Smart parse (brand + multi-category):', {
        original: collectionSlug,
        brand: detectedBrand,
        categories: multiCategoryCollections[remainingSlug]
      });
      return {
        collection: 'all',
        brand: detectedBrand,
        categories: multiCategoryCollections[remainingSlug]
      };
    }

    // Step 1.6: Check if remaining slug matches multi-product-type collection
    if (multiProductTypeCollections[remainingSlug]) {
      console.log('[ReluxeFilters] Smart parse (brand + multi-product-type):', {
        original: collectionSlug,
        brand: detectedBrand,
        product_types: multiProductTypeCollections[remainingSlug]
      });
      return {
        collection: 'all',
        brand: detectedBrand,
        product_types: multiProductTypeCollections[remainingSlug]
      };
    }

    // Step 2: Extract product type FIRST (more specific than category)
    // Check multi-word product types first (longest first)
    const sortedProductTypes = Object.entries(productTypeKeywords).sort((a, b) => b[0].length - a[0].length);
    for (const [keyword, type] of sortedProductTypes) {
      if (remainingSlug.includes(keyword)) {
        detectedProductType = type;
        remainingSlug = remainingSlug.replace(keyword, '').replace(/^-+|-+$/g, '').replace(/-{2,}/g, '-');
        break;
      }
    }

    // Step 3: Extract category (only if no product type found, to avoid over-filtering)
    if (!detectedProductType) {
      const sortedCategories = Object.entries(categoryKeywords).sort((a, b) => b[0].length - a[0].length);
      for (const [keyword, cat] of sortedCategories) {
        if (remainingSlug.includes(keyword)) {
          detectedCategory = cat;
          remainingSlug = remainingSlug.replace(keyword, '').replace(/^-+|-+$/g, '').replace(/-{2,}/g, '-');
          break;
        }
      }
    }

    // Step 4: Use remaining words as search query
    // Convert hyphens to spaces and clean up
    let searchQuery = remainingSlug.replace(/-/g, ' ').trim();

    // Remove common filler/connective words that don't add search value
    // These words are often used in URLs but may not appear in product titles
    const fillerWords = [
      'all', 'the', 'and', 'for', 'with', 'by', 'in', 'to', 'of', 'on', 'a', 'an',
      'from', 'at', 'is', 'it', 'as', 'or', 'be', 'this', 'that', 'new', 'shop'
    ];
    searchQuery = searchQuery.split(' ').filter(word => !fillerWords.includes(word) && word.length > 1).join(' ');

    console.log('[ReluxeFilters] Smart parse:', {
      original: collectionSlug,
      brand: detectedBrand,
      category: detectedCategory,
      product_type: detectedProductType,
      search: searchQuery || null
    });

    return {
      collection: 'all',
      brand: detectedBrand,
      category: detectedCategory,
      product_type: detectedProductType,
      search: searchQuery || null
    };
  }

  // Build Algolia filters string
  function buildAlgoliaFilters() {
    const filters = [];

    // Always filter by active status
    filters.push('status:active');

    // Department filter (include Unisex items in both Women and Men collections)
    if (collectionFilters.department) {
      filters.push(`(department:"${collectionFilters.department}" OR department:"Unisex")`);
    } else if (currentFilters.department) {
      filters.push(`(department:"${currentFilters.department}" OR department:"Unisex")`);
    }

    // Brand filters (can be multiple)
    if (collectionFilters.brand) {
      filters.push(`brand:"${collectionFilters.brand}"`);
    } else if (currentFilters.brands.length > 0) {
      const brandFilters = currentFilters.brands.map(b => `brand:"${b}"`).join(' OR ');
      filters.push(`(${brandFilters})`);
    }

    // Category filter (multi-select)
    if (collectionFilters.categories && collectionFilters.categories.length > 0) {
      // Multiple categories from multi-category collections (e.g., apparel)
      const categoryFilters = collectionFilters.categories.map(c => `category:"${c}"`).join(' OR ');
      filters.push(`(${categoryFilters})`);
      console.log('[ReluxeFilters] Applied categories filter:', categoryFilters);
    } else if (collectionFilters.category) {
      filters.push(`category:"${collectionFilters.category}"`);
    } else if (currentFilters.categories.length > 0) {
      const categoryFilters = currentFilters.categories.map(c => `category:"${c}"`).join(' OR ');
      filters.push(`(${categoryFilters})`);
    }

    // Product type filter (multi-select)
    if (collectionFilters.product_types && collectionFilters.product_types.length > 0) {
      // Array of product types from search collections
      const typeFilters = collectionFilters.product_types.map(t => `product_type:"${t}"`).join(' OR ');
      filters.push(`(${typeFilters})`);
    } else if (collectionFilters.product_type) {
      filters.push(`product_type:"${collectionFilters.product_type}"`);
    } else if (currentFilters.product_types.length > 0) {
      const typeFilters = currentFilters.product_types.map(t => `product_type:"${t}"`).join(' OR ');
      filters.push(`(${typeFilters})`);
    }

    // Color filter (multi-select)
    if (currentFilters.colors.length > 0) {
      const colorFilters = currentFilters.colors.map(c => `color:"${c}"`).join(' OR ');
      filters.push(`(${colorFilters})`);
    }

    // Condition filter (multi-select)
    if (currentFilters.conditions.length > 0) {
      const conditionFilters = currentFilters.conditions.map(c => `condition:"${c}"`).join(' OR ');
      filters.push(`(${conditionFilters})`);
    }

    // Material filter (multi-select)
    if (currentFilters.materials.length > 0) {
      const materialFilters = currentFilters.materials.map(m => `material:"${m}"`).join(' OR ');
      filters.push(`(${materialFilters})`);
    }

    // Size filter (multi-select) — uses size (US) or size_eu based on toggle
    if (currentFilters.sizes.length > 0) {
      const sizeField = currentFilters.size_system === 'eu' ? 'size_eu' : 'size';
      const sizeFilters = currentFilters.sizes.map(s => `${sizeField}:"${s}"`).join(' OR ');
      filters.push(`(${sizeFilters})`);
    }

    // Price ranges (multi-select) - calculate overall min/max from selected ranges
    if (currentFilters.price_ranges.length > 0) {
      // Find the lowest min and highest max across all selected ranges
      let overallMin = Infinity;
      let overallMax = -Infinity;

      currentFilters.price_ranges.forEach(range => {
        if (range.min !== null && range.min < overallMin) overallMin = range.min;
        if (range.max !== null && range.max > overallMax) overallMax = range.max;
        // Handle open-ended ranges
        if (range.min !== null && range.max === null) overallMax = Infinity;
        if (range.max !== null && range.min === null) overallMin = 0;
      });

      // Apply combined filter
      if (overallMin !== Infinity && overallMin > 0) {
        filters.push(`price >= ${overallMin}`);
      }
      if (overallMax !== -Infinity && overallMax !== Infinity) {
        filters.push(`price <= ${overallMax}`);
      }
    }

    // Custom price range (min/max inputs)
    if (currentFilters.custom_min_price !== null) {
      filters.push(`price >= ${currentFilters.custom_min_price}`);
    }
    if (currentFilters.custom_max_price !== null) {
      filters.push(`price <= ${currentFilters.custom_max_price}`);
    }

    // New arrivals - filter by timestamp (last 14 days)
    if (collectionFilters.newArrivals && collectionFilters.minTimestamp) {
      filters.push(`created_at_timestamp >= ${collectionFilters.minTimestamp}`);
    }

    // Sale collection - filter by "Sale" tag
    if (collectionFilters.saleTag) {
      filters.push(`(tags:"Sale" OR tags:"sale")`);
    }

    // Valentine's Day 2026 collection - filter by "vday" tag
    if (collectionFilters.vdayTag) {
      filters.push(`tags:"vday"`);
    }

    // Mother's Day 2026 collection - filter by "mday26" tag
    if (collectionFilters.mothersDayTag) {
      filters.push(`tags:"mday26"`);
    }

    // Editor's Picks - curated, filter by "editors-pick" tag
    if (collectionFilters.editorsPickTag) {
      filters.push(`tags:"editors-pick"`);
    }

    // Icons - curated, filter by "icons" tag
    if (collectionFilters.iconsTag) {
      filters.push(`tags:"icons"`);
    }

    // Pretty in Pink - pink bags + wallets only (skews ~70/30 naturally)
    if (collectionFilters.prettyInPinkTag) {
      filters.push(`color:"Pink" AND (category:"Bags" OR product_type:"Wallets")`);
    }

    const finalFilter = filters.join(' AND ');
    console.log('[ReluxeFilters] FULL FILTER STRING:', finalFilter);
    return finalFilter;
  }

  // Get the correct index name based on sort option
  function getAlgoliaIndexName() {
    switch (currentFilters.sort) {
      case 'newest':
        return 'products_newest';
      case 'price_asc':
        return 'products_price_asc';
      case 'price_desc':
        return 'products_price_desc';
      default:
        return 'products_newest';  // Default to newest
    }
  }

  // Build filters for facet query (excludes the facet being queried for disjunctive behavior)
  function buildFacetFilters(excludeFacet) {
    const filters = [];

    // Always filter by active status
    filters.push('status:active');

    // Department filter (include Unisex items in both Women and Men collections)
    // ALWAYS apply collection-level department filter (from URL)
    if (collectionFilters.department) {
      filters.push(`(department:"${collectionFilters.department}" OR department:"Unisex")`);
    }
    // User-selected department - use disjunctive faceting (exclude when getting department facets)
    if (excludeFacet !== 'department' && currentFilters.department) {
      filters.push(`(department:"${currentFilters.department}" OR department:"Unisex")`);
    }

    // Brand filters
    // ALWAYS apply collection-level brand filter (from URL) - don't use disjunctive for collection filters
    if (collectionFilters.brand) {
      filters.push(`brand:"${collectionFilters.brand}"`);
    }
    // User-selected brands - use disjunctive faceting (exclude when getting brand facets)
    if (excludeFacet !== 'brand' && currentFilters.brands.length > 0) {
      const brandFilters = currentFilters.brands.map(b => `brand:"${b}"`).join(' OR ');
      filters.push(`(${brandFilters})`);
    }

    // Category filter
    // ALWAYS apply collection-level category filters (from URL)
    if (collectionFilters.categories && collectionFilters.categories.length > 0) {
      const categoryFilters = collectionFilters.categories.map(c => `category:"${c}"`).join(' OR ');
      filters.push(`(${categoryFilters})`);
    } else if (collectionFilters.category) {
      filters.push(`category:"${collectionFilters.category}"`);
    }
    // User-selected categories - use disjunctive faceting
    if (excludeFacet !== 'category' && currentFilters.categories.length > 0) {
      const categoryFilters = currentFilters.categories.map(c => `category:"${c}"`).join(' OR ');
      filters.push(`(${categoryFilters})`);
    }

    // Product type filter
    // ALWAYS apply collection-level product_type filters (from URL)
    if (collectionFilters.product_types && collectionFilters.product_types.length > 0) {
      const typeFilters = collectionFilters.product_types.map(t => `product_type:"${t}"`).join(' OR ');
      filters.push(`(${typeFilters})`);
    } else if (collectionFilters.product_type) {
      filters.push(`product_type:"${collectionFilters.product_type}"`);
    }
    // User-selected product types - use disjunctive faceting
    if (excludeFacet !== 'product_type' && currentFilters.product_types.length > 0) {
      const typeFilters = currentFilters.product_types.map(t => `product_type:"${t}"`).join(' OR ');
      filters.push(`(${typeFilters})`);
    }

    // Color filter - exclude if we're getting color facets
    if (excludeFacet !== 'color') {
      if (currentFilters.colors.length > 0) {
        const colorFilters = currentFilters.colors.map(c => `color:"${c}"`).join(' OR ');
        filters.push(`(${colorFilters})`);
      }
    }

    // Condition filter - exclude if we're getting condition facets
    if (excludeFacet !== 'condition') {
      if (currentFilters.conditions.length > 0) {
        const conditionFilters = currentFilters.conditions.map(c => `condition:"${c}"`).join(' OR ');
        filters.push(`(${conditionFilters})`);
      }
    }

    // Material filter - exclude if we're getting material facets
    if (excludeFacet !== 'material') {
      if (currentFilters.materials.length > 0) {
        const materialFilters = currentFilters.materials.map(m => `material:"${m}"`).join(' OR ');
        filters.push(`(${materialFilters})`);
      }
    }

    // Size filter - exclude if we're getting size facets
    if (excludeFacet !== 'size' && excludeFacet !== 'size_eu') {
      if (currentFilters.sizes.length > 0) {
        const sizeField = currentFilters.size_system === 'eu' ? 'size_eu' : 'size';
        const sizeFilters = currentFilters.sizes.map(s => `${sizeField}:"${s}"`).join(' OR ');
        filters.push(`(${sizeFilters})`);
      }
    }

    // Price filters always apply
    if (currentFilters.price_ranges.length > 0) {
      let overallMin = Infinity;
      let overallMax = -Infinity;
      currentFilters.price_ranges.forEach(range => {
        if (range.min !== null && range.min < overallMin) overallMin = range.min;
        if (range.max !== null && range.max > overallMax) overallMax = range.max;
        if (range.min !== null && range.max === null) overallMax = Infinity;
        if (range.max !== null && range.min === null) overallMin = 0;
      });
      if (overallMin !== Infinity && overallMin > 0) {
        filters.push(`price >= ${overallMin}`);
      }
      if (overallMax !== -Infinity && overallMax !== Infinity) {
        filters.push(`price <= ${overallMax}`);
      }
    }

    if (currentFilters.custom_min_price !== null) {
      filters.push(`price >= ${currentFilters.custom_min_price}`);
    }
    if (currentFilters.custom_max_price !== null) {
      filters.push(`price <= ${currentFilters.custom_max_price}`);
    }

    // New arrivals timestamp
    if (collectionFilters.newArrivals && collectionFilters.minTimestamp) {
      filters.push(`created_at_timestamp >= ${collectionFilters.minTimestamp}`);
    }

    // Sale tag filter
    if (collectionFilters.saleTag) {
      filters.push(`(tags:"Sale" OR tags:"sale")`);
    }

    // Valentine's Day 2026 collection - filter by "vday" tag
    if (collectionFilters.vdayTag) {
      filters.push(`tags:"vday"`);
    }

    // Mother's Day 2026 collection - filter by "mday26" tag
    if (collectionFilters.mothersDayTag) {
      filters.push(`tags:"mday26"`);
    }

    // Editor's Picks - curated, filter by "editors-pick" tag
    if (collectionFilters.editorsPickTag) {
      filters.push(`tags:"editors-pick"`);
    }

    // Icons - curated, filter by "icons" tag
    if (collectionFilters.iconsTag) {
      filters.push(`tags:"icons"`);
    }

    // Pretty in Pink - pink bags + wallets only (skews ~70/30 naturally)
    if (collectionFilters.prettyInPinkTag) {
      filters.push(`color:"Pink" AND (category:"Bags" OR product_type:"Wallets")`);
    }

    const finalFilter = filters.join(' AND ');
    console.log('[ReluxeFilters] FACET FILTER STRING:', finalFilter);
    return finalFilter;
  }

  // Fetch from Algolia with disjunctive faceting
  async function fetchFilters() {
    console.log('[ReluxeFilters] fetchFilters called');
    if (isLoading) {
      console.log('[ReluxeFilters] Already loading, returning');
      return;
    }
    isLoading = true;

    showLoading();

    console.log('[ReluxeFilters] Algolia search starting...');
    console.log('[ReluxeFilters] searchClient:', searchClient ? 'exists' : 'null');
    console.log('[ReluxeFilters] currentFilters:', JSON.stringify(currentFilters));
    console.log('[ReluxeFilters] collectionFilters:', JSON.stringify(collectionFilters));
    console.time('[ReluxeFilters] Algolia fetch');

    try {
      // Use the correct index based on sort option
      const indexName = getAlgoliaIndexName();
      const currentIndex = searchClient.initIndex(indexName);

      // For collection-based searches (mini-bags, travel-bags), use OR logic for words
      // This makes all search words optional so "mini nano micro petit bag" matches ANY of those
      const useOptionalWords = collectionFilters.search && currentFilters.search === collectionFilters.search;
      const optionalWordsParam = useOptionalWords ? { optionalWords: currentFilters.search } : {};

      // Main query for products (with all filters)
      // If restrictToTitle is set, only search in title field
      const restrictTitleParam = collectionFilters.restrictToTitle ? { restrictSearchableAttributes: ['title'] } : {};

      const mainQuery = {
        indexName: indexName,
        query: currentFilters.search || '',
        params: {
          page: currentFilters.page,
          hitsPerPage: currentFilters.per_page,
          filters: buildAlgoliaFilters(),
          facets: ['brand', 'category', 'product_type', 'color', 'condition', 'material', 'department', 'size'],
          attributesToRetrieve: ['objectID', 'sku', 'title', 'brand', 'price', 'category', 'product_type', 'color', 'condition', 'material', 'department', 'size', 'image', 'image2', 'handle', 'views_count', 'available', 'inventory_quantity', 'badges'],
          attributesToHighlight: [],
          clickAnalytics: true,
          ...optionalWordsParam,
          ...restrictTitleParam
        }
      };

      // Disjunctive facet queries - get facets WITHOUT that facet's filter applied
      const brandFacetQuery = {
        indexName: indexName,
        query: currentFilters.search || '',
        params: {
          hitsPerPage: 0,
          filters: buildFacetFilters('brand'),
          facets: ['brand'],
          maxValuesPerFacet: 100,
          ...optionalWordsParam,
          ...restrictTitleParam
        }
      };

      const categoryFacetQuery = {
        indexName: indexName,
        query: currentFilters.search || '',
        params: {
          hitsPerPage: 0,
          filters: buildFacetFilters('category'),
          facets: ['category'],
          maxValuesPerFacet: 50,
          ...optionalWordsParam,
          ...restrictTitleParam
        }
      };

      const productTypeFacetQuery = {
        indexName: indexName,
        query: currentFilters.search || '',
        params: {
          hitsPerPage: 0,
          filters: buildFacetFilters('product_type'),
          facets: ['product_type'],
          maxValuesPerFacet: 100,
          ...optionalWordsParam,
          ...restrictTitleParam
        }
      };

      const colorFacetQuery = {
        indexName: indexName,
        query: currentFilters.search || '',
        params: {
          hitsPerPage: 0,
          filters: buildFacetFilters('color'),
          facets: ['color'],
          maxValuesPerFacet: 100,
          ...optionalWordsParam,
          ...restrictTitleParam
        }
      };

      const departmentFacetQuery = {
        indexName: indexName,
        query: currentFilters.search || '',
        params: {
          hitsPerPage: 0,
          filters: buildFacetFilters('department'),
          facets: ['department'],
          ...optionalWordsParam,
          ...restrictTitleParam
        }
      };

      const materialFacetQuery = {
        indexName: indexName,
        query: currentFilters.search || '',
        params: {
          hitsPerPage: 0,
          filters: buildFacetFilters('material'),
          facets: ['material'],
          maxValuesPerFacet: 50,
          ...optionalWordsParam,
          ...restrictTitleParam
        }
      };

      const conditionFacetQuery = {
        indexName: indexName,
        query: currentFilters.search || '',
        params: {
          hitsPerPage: 0,
          filters: buildFacetFilters('condition'),
          facets: ['condition'],
          maxValuesPerFacet: 10,
          ...optionalWordsParam,
          ...restrictTitleParam
        }
      };

      const sizeFacetField = currentFilters.size_system === 'eu' ? 'size_eu' : 'size';
      const sizeFacetQuery = {
        indexName: indexName,
        query: currentFilters.search || '',
        params: {
          hitsPerPage: 0,
          filters: buildFacetFilters(sizeFacetField),
          facets: [sizeFacetField],
          maxValuesPerFacet: 100,
          ...optionalWordsParam,
          ...restrictTitleParam
        }
      };

      console.log('[ReluxeFilters] Running disjunctive facet queries...');

      // Run all queries in parallel using multipleQueries
      const multiResults = await searchClient.multipleQueries([
        mainQuery,
        brandFacetQuery,
        categoryFacetQuery,
        productTypeFacetQuery,
        colorFacetQuery,
        departmentFacetQuery,
        conditionFacetQuery,
        materialFacetQuery,
        sizeFacetQuery
      ]);

      const results = multiResults.results[0];  // Main results
      const brandFacets = multiResults.results[1].facets?.brand || {};
      const categoryFacets = multiResults.results[2].facets?.category || {};
      const productTypeFacets = multiResults.results[3].facets?.product_type || {};
      const colorFacets = multiResults.results[4].facets?.color || {};
      const departmentFacets = multiResults.results[5].facets?.department || {};
      const conditionFacets = multiResults.results[6].facets?.condition || {};
      const materialFacets = multiResults.results[7].facets?.material || {};
      const sizeFacets = multiResults.results[8].facets?.[sizeFacetField] || {};

      // Capture queryID and hits for Insights event tracking
      currentQueryID = results.queryID || null;
      currentHits = results.hits.map(h => h.objectID);

      console.timeEnd('[ReluxeFilters] Algolia fetch');
      console.log('[ReluxeFilters] Algolia response:', results.nbHits, 'hits in', results.processingTimeMS, 'ms', currentQueryID ? '(queryID: ' + currentQueryID + ')' : '');

      // Combine disjunctive facets
      const disjunctiveFacets = {
        brand: brandFacets,
        category: categoryFacets,
        product_type: productTypeFacets,
        color: colorFacets,
        department: departmentFacets,
        condition: conditionFacets,
        material: materialFacets,
        size: sizeFacets
      };

      // Transform Algolia results to match our expected format
      filterData = transformAlgoliaResults(results, disjunctiveFacets);
      facetData = results.facets || {};

      console.time('[ReluxeFilters] Render');
      renderSidebar();
      renderProducts();
      renderPagination();
      console.timeEnd('[ReluxeFilters] Render');

      // Track search analytics
      if (currentFilters.search && currentFilters.page === 0) {
        trackSearchQuery(currentFilters.search, filterData.total);
      }

      // Algolia Insights: send view event for displayed results
      if (currentHits.length > 0) {
        // Send up to 20 objectIDs per view event (Algolia limit)
        const viewBatch = currentHits.slice(0, 20);
        sendAlgoliaEvent('view', {
          eventType: 'view',
          eventName: 'Products Viewed',
          index: indexName,
          objectIDs: viewBatch
        });
      }

    } catch (error) {
      console.error('[ReluxeFilters] Algolia error:', error);
      showError('Unable to load products. Please refresh the page. Error: ' + error.message);
    } finally {
      isLoading = false;
      hideLoading();
    }
  }

  // Round-robin items across brands so no single brand clumps together.
  // Preserves each brand's existing (newest) internal order.
  function spreadByBrand(items) {
    const byBrand = {};
    const order = [];
    items.forEach(it => {
      const b = it.brand || '?';
      if (!byBrand[b]) { byBrand[b] = []; order.push(b); }
      byBrand[b].push(it);
    });
    const out = [];
    while (out.length < items.length) {
      for (const b of order) {
        if (byBrand[b].length) out.push(byBrand[b].shift());
      }
    }
    return out;
  }

  // Transform Algolia results to match expected format
  function transformAlgoliaResults(results, cachedFacets) {
    let products = results.hits.map(hit => ({
      sku: hit.sku || hit.objectID,
      title: hit.title || '',
      brand: hit.brand || '',
      price: hit.price || 0,
      category: hit.category || '',
      product_type: hit.product_type || '',
      color: hit.color || '',
      department: hit.department || '',
      image: hit.image || '',
      image2: hit.image2 || '',
      handle: hit.handle || '',
      available: hit.available,
      inventory_quantity: hit.inventory_quantity || 0,
      views_count: hit.views_count || 0,
      badges: hit.badges || []
    }));

    // Curated collections (Icons): the storefront sorts by "newest", which clumps
    // same-brand batches together (e.g. a block of Rolex added the same day).
    // Spread brands round-robin within the page so no single brand clusters.
    if (collectionFilters.iconsTag) {
      products = spreadByBrand(products);
    }

    // Use disjunctive facets - these contain ALL options with correct counts
    // Each facet query excludes its own filter, so counts reflect what's available
    const disjunctiveFacets = cachedFacets || {};

    // Helper: convert facet object to sorted array
    function buildFacetList(facetObj) {
      if (!facetObj) return [];
      return Object.entries(facetObj)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count);
    }

    // Condition in canonical order (best → needs love)
    const CONDITION_ORDER = ['New', 'Excellent', 'Very Good', 'Good', 'Fair'];
    const conditionList = buildFacetList(disjunctiveFacets.condition)
      .sort((a, b) => CONDITION_ORDER.indexOf(a.name) - CONDITION_ORDER.indexOf(b.name));

    // Material sorted by count descending
    const materialList = buildFacetList(disjunctiveFacets.material);

    // Size: smart sort — letter sizes in order, then numeric ascending
    const SIZE_ORDER = ['XXXS', 'XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', 'XXXXL', 'OS'];
    const sizeList = buildFacetList(disjunctiveFacets.size)
      .filter(s => s.name && s.name.trim() !== '')
      .sort((a, b) => {
        const aIdx = SIZE_ORDER.indexOf(a.name.toUpperCase());
        const bIdx = SIZE_ORDER.indexOf(b.name.toUpperCase());
        if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
        if (aIdx !== -1) return -1;
        if (bIdx !== -1) return 1;
        const aNum = parseFloat(a.name);
        const bNum = parseFloat(b.name);
        if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
        return a.name.localeCompare(b.name);
      });

    // Build filters object using disjunctive facet counts directly
    const filters = {
      brands: buildFacetList(disjunctiveFacets.brand),
      categories: buildFacetList(disjunctiveFacets.category),
      product_types: buildFacetList(disjunctiveFacets.product_type),
      colors: buildFacetList(disjunctiveFacets.color),
      conditions: conditionList,
      materials: materialList,
      sizes: sizeList,
      departments: buildFacetList(disjunctiveFacets.department),
      price_range: { min: 0, max: 50000 }  // Default range
    };

    return {
      products,
      total: results.nbHits,
      page: results.page + 1,  // Convert to 1-indexed for display
      per_page: results.hitsPerPage,
      total_pages: results.nbPages,
      filters,
      applied_filters: {
        brands: currentFilters.brands,
        categories: currentFilters.categories,
        product_types: currentFilters.product_types,
        colors: currentFilters.colors,
        conditions: currentFilters.conditions,
        materials: currentFilters.materials,
        sizes: currentFilters.sizes,
        department: currentFilters.department,
        price_ranges: currentFilters.price_ranges,
        custom_min_price: currentFilters.custom_min_price,
        custom_max_price: currentFilters.custom_max_price
      },
      processing_time_ms: results.processingTimeMS
    };
  }

  // Track search analytics
  function trackSearchQuery(query, resultCount) {
    const customerId = getCustomerId();
    fetch(`${API_BASE}/api/search-analytics`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: query,
        result_count: resultCount,
        customer_id: customerId
      }),
      credentials: 'include'
    }).catch(() => {});
  }

  // Count active filters
  function countActiveFilters() {
    let count = 0;
    if (currentFilters.brands.length > 0) count += currentFilters.brands.length;
    if (currentFilters.categories.length > 0) count += currentFilters.categories.length;
    if (currentFilters.product_types.length > 0) count += currentFilters.product_types.length;
    if (currentFilters.colors.length > 0) count += currentFilters.colors.length;
    if (currentFilters.price_ranges.length > 0) count += currentFilters.price_ranges.length;
    if (currentFilters.custom_min_price !== null || currentFilters.custom_max_price !== null) count += 1;
    return count;
  }

  // Render mobile toggle button
  function renderMobileToggle() {
    let toggleBtn = document.querySelector('.reluxe-mobile-filter-toggle');
    const toolbar = document.querySelector('.reluxe-toolbar');
    const sidebar = document.getElementById('reluxe-filter-sidebar');

    if (!sidebar) return;

    // If toolbar exists, put button there; otherwise put before sidebar (fallback)
    if (!toggleBtn) {
      toggleBtn = document.createElement('button');
      toggleBtn.className = 'reluxe-mobile-filter-toggle';
      toggleBtn.onclick = function() {
        ReluxeFilters.toggleMobileFilters();
      };

      if (toolbar) {
        // Insert at the beginning of toolbar (before sort dropdown)
        const sortDiv = toolbar.querySelector('.reluxe-sort');
        if (sortDiv) {
          toolbar.insertBefore(toggleBtn, sortDiv);
        } else {
          toolbar.appendChild(toggleBtn);
        }
      } else {
        // Fallback: insert before sidebar
        sidebar.parentNode.insertBefore(toggleBtn, sidebar);
      }
    }

    const activeCount = countActiveFilters();
    const badgeHtml = activeCount > 0 ? `<span class="reluxe-filter-count-badge">${activeCount}</span>` : '';
    const isOpen = sidebar.classList.contains('reluxe-sidebar-open');

    toggleBtn.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
      </svg>
      ${isOpen ? 'Hide' : 'Filter'}${badgeHtml}
    `;
  }

  // Color map for visual swatches - including composite colors
  const colorSwatches = {
    'Black': '#000000',
    'White': '#FFFFFF',
    'Brown': '#8B4513',
    'Tan': '#D2B48C',
    'Beige': '#F5F5DC',
    'Navy': '#000080',
    'Blue': '#0066CC',
    'Red': '#CC0000',
    'Pink': '#FFC0CB',
    'Green': '#228B22',
    'Gray': '#808080',
    'Grey': '#808080',
    'Gold': '#FFD700',
    'Silver': '#C0C0C0',
    'Orange': '#FF8C00',
    'Yellow': '#FFD700',
    'Purple': '#800080',
    'Burgundy': '#800020',
    'Cream': '#FFFDD0',
    'Ivory': '#FFFFF0',
    'Camel': '#C19A6B',
    'Cognac': '#9A463D',
    'Nude': '#E3BC9A',
    'Rose': '#FF007F',
    'Coral': '#FF7F50',
    'Turquoise': '#40E0D0',
    'Teal': '#008080',
    'Olive': '#808000',
    'Khaki': '#C3B091',
    'Multicolor': 'linear-gradient(135deg, #ff0000, #ff7f00, #ffff00, #00ff00, #0000ff, #8b00ff)',
    'Multi': 'linear-gradient(135deg, #ff0000, #ff7f00, #ffff00, #00ff00, #0000ff, #8b00ff)'
  };

  // Composite color mappings (two-tone colors)
  const compositeColors = {
    'Black Gold': ['#000000', '#FFD700'],
    'Black Red': ['#000000', '#CC0000'],
    'Black White': ['#000000', '#FFFFFF'],
    'Black Silver': ['#000000', '#C0C0C0'],
    'Black Brown': ['#000000', '#8B4513'],
    'Brown Gold': ['#8B4513', '#FFD700'],
    'Brown Beige': ['#8B4513', '#F5F5DC'],
    'Navy White': ['#000080', '#FFFFFF'],
    'Red White': ['#CC0000', '#FFFFFF'],
    'Blue White': ['#0066CC', '#FFFFFF'],
    'Pink White': ['#FFC0CB', '#FFFFFF'],
    'Gold Silver': ['#FFD700', '#C0C0C0'],
    'Beige Brown': ['#F5F5DC', '#8B4513'],
    'Cream Brown': ['#FFFDD0', '#8B4513']
  };

  function getColorInfo(colorName) {
    // Check if it's a composite color
    if (compositeColors[colorName]) {
      return {
        isComposite: true,
        colors: compositeColors[colorName]
      };
    }

    // Check if colorName contains two color words (e.g., "Black Gold")
    const words = colorName.split(' ');
    if (words.length === 2) {
      const color1 = colorSwatches[words[0]];
      const color2 = colorSwatches[words[1]];
      if (color1 && color2 && !color1.includes('gradient') && !color2.includes('gradient')) {
        return {
          isComposite: true,
          colors: [color1, color2]
        };
      }
    }

    // Single color
    const color = colorSwatches[colorName];
    if (!color) return { isComposite: false, color: '#ccc' };
    return { isComposite: false, color: color };
  }

  function renderColorSwatch(colorName) {
    const info = getColorInfo(colorName);
    const needsBorder = ['White', 'Cream', 'Beige', 'Ivory', 'Nude'].includes(colorName) ||
                        colorName.includes('White') || colorName.includes('Cream');

    if (info.isComposite) {
      // Two-tone color with diagonal split
      return `<span class="reluxe-color-swatch reluxe-color-swatch--composite ${needsBorder ? 'reluxe-color-swatch--bordered' : ''}"
                    style="--color1: ${info.colors[0]}; --color2: ${info.colors[1]};">
                <span style="position:absolute;top:0;left:0;width:100%;height:100%;background:${info.colors[0]};clip-path:polygon(0 0, 100% 0, 0 100%);"></span>
                <span style="position:absolute;top:0;left:0;width:100%;height:100%;background:${info.colors[1]};clip-path:polygon(100% 0, 100% 100%, 0 100%);"></span>
              </span>`;
    } else {
      // Single color
      const style = info.color.includes('gradient') ? `background: ${info.color};` : `background: ${info.color};`;
      return `<span class="reluxe-color-swatch ${needsBorder ? 'reluxe-color-swatch--bordered' : ''}" style="${style}"></span>`;
    }
  }

  // Render filter sidebar
  function renderSidebar() {
    const sidebar = document.getElementById('reluxe-filter-sidebar');
    if (!sidebar || !filterData) return;

    const { filters, applied_filters } = filterData;

    renderMobileToggle();

    // Search header
    const searchHeader = currentFilters.search ? `
      <div class="reluxe-search-query">
        <span class="reluxe-search-query__label">Results for:</span>
        <span class="reluxe-search-query__term">"${currentFilters.search}"</span>
      </div>
    ` : '';

    let html = `
      ${searchHeader}
      <div class="reluxe-filter-header">
        <h3>Filter By</h3>
        <button class="reluxe-clear-all" onclick="ReluxeFilters.clearAll()">Clear All</button>
      </div>
    `;

    // Brand filter (first) - hide if collection is already filtered to a single brand
    if (filters.brands && filters.brands.length > 0 && !collectionFilters.brand) {
      html += `
        <div class="reluxe-filter-group">
          <div class="reluxe-filter-title" onclick="ReluxeFilters.toggleGroup(this)">
            Brand <span class="reluxe-toggle">−</span>
          </div>
          <div class="reluxe-filter-options reluxe-scrollable">
            <input type="text" class="reluxe-filter-search"
              placeholder="Search brands..."
              oninput="ReluxeFilters.filterBrandList(this.value)">
            <div class="reluxe-brand-list">
      `;

      filters.brands.forEach(brand => {
        const isChecked = applied_filters.brands.includes(brand.name);
        html += `
          <label class="reluxe-filter-option" data-brand="${brand.name.toLowerCase()}">
            <input type="checkbox" value="${brand.name}"
              ${isChecked ? 'checked' : ''}
              onchange="ReluxeFilters.toggleBrand('${brand.name.replace(/'/g, "\\'")}')">
            <span class="reluxe-checkbox ${isChecked ? 'checked' : ''}"></span>
            <span class="reluxe-filter-label">${brand.name}</span>
            <span class="reluxe-count">(${brand.count})</span>
          </label>
        `;
      });

      html += `
            </div>
          </div>
        </div>
      `;
    }

    // Department filter (Women/Men) - hide if collection is already filtered by department
    // Only show when there are items in both departments
    if (!collectionFilters.department && filters.departments && filters.departments.length > 1) {
      html += `
        <div class="reluxe-filter-group">
          <div class="reluxe-filter-title" onclick="ReluxeFilters.toggleGroup(this)">
            Department <span class="reluxe-toggle">−</span>
          </div>
          <div class="reluxe-filter-options">
      `;

      // Only show Women and Men (Unisex items will be included in both)
      filters.departments.forEach(dept => {
        // Skip Unisex in the UI - it's automatically included in Women/Men filters
        if (dept.name === 'Unisex') return;
        const isChecked = currentFilters.department === dept.name;
        html += `
          <label class="reluxe-filter-option">
            <input type="checkbox" value="${dept.name}"
              ${isChecked ? 'checked' : ''}
              onchange="ReluxeFilters.toggleDepartment('${dept.name}')">
            <span class="reluxe-checkbox ${isChecked ? 'checked' : ''}"></span>
            <span class="reluxe-filter-label">${dept.name}</span>
            <span class="reluxe-count">(${dept.count})</span>
          </label>
        `;
      });

      html += `
          </div>
        </div>
      `;
    }

    // Category filter (checkboxes) - hide if collection is already filtered to a single category
    if (filters.categories && filters.categories.length > 0 && !collectionFilters.category) {
      html += `
        <div class="reluxe-filter-group">
          <div class="reluxe-filter-title" onclick="ReluxeFilters.toggleGroup(this)">
            Category <span class="reluxe-toggle">−</span>
          </div>
          <div class="reluxe-filter-options reluxe-scrollable">
      `;

      filters.categories.forEach(cat => {
        const isChecked = applied_filters.categories && applied_filters.categories.includes(cat.name);
        html += `
          <label class="reluxe-filter-option">
            <input type="checkbox" value="${cat.name}"
              ${isChecked ? 'checked' : ''}
              onchange="ReluxeFilters.toggleCategory('${cat.name.replace(/'/g, "\\'")}')">
            <span class="reluxe-checkbox ${isChecked ? 'checked' : ''}"></span>
            <span class="reluxe-filter-label">${cat.name}</span>
            <span class="reluxe-count">(${cat.count})</span>
          </label>
        `;
      });

      html += `
          </div>
        </div>
      `;
    }

    // Product Type filter (checkboxes) - hide if collection is already filtered to a single product type
    if (filters.product_types && filters.product_types.length > 0 && !collectionFilters.product_type) {
      html += `
        <div class="reluxe-filter-group">
          <div class="reluxe-filter-title" onclick="ReluxeFilters.toggleGroup(this)">
            Product Type <span class="reluxe-toggle">−</span>
          </div>
          <div class="reluxe-filter-options reluxe-scrollable">
      `;

      filters.product_types.forEach(type => {
        const isChecked = applied_filters.product_types && applied_filters.product_types.includes(type.name);
        html += `
          <label class="reluxe-filter-option">
            <input type="checkbox" value="${type.name}"
              ${isChecked ? 'checked' : ''}
              onchange="ReluxeFilters.toggleProductType('${type.name.replace(/'/g, "\\'")}')">
            <span class="reluxe-checkbox ${isChecked ? 'checked' : ''}"></span>
            <span class="reluxe-filter-label">${type.name}</span>
            <span class="reluxe-count">(${type.count})</span>
          </label>
        `;
      });

      html += `
          </div>
        </div>
      `;
    }

    // Price range filter (multi-select checkboxes)
    const priceRanges = [
      { min: 0, max: 500, label: 'Under $500' },
      { min: 500, max: 1000, label: '$500 - $1,000' },
      { min: 1000, max: 2500, label: '$1,000 - $2,500' },
      { min: 2500, max: 5000, label: '$2,500 - $5,000' },
      { min: 5000, max: 10000, label: '$5,000 - $10,000' },
      { min: 10000, max: null, label: '$10,000+' }
    ];

    // Helper to check if a price range is selected
    const isPriceRangeActive = (min, max) => {
      return applied_filters.price_ranges && applied_filters.price_ranges.some(r => r.min === min && r.max === max);
    };

    html += `
      <div class="reluxe-filter-group">
        <div class="reluxe-filter-title" onclick="ReluxeFilters.toggleGroup(this)">
          Price <span class="reluxe-toggle">−</span>
        </div>
        <div class="reluxe-filter-options">
          <div class="reluxe-price-ranges">
    `;

    priceRanges.forEach(range => {
      const isActive = isPriceRangeActive(range.min, range.max);
      html += `
        <label class="reluxe-price-range-option ${isActive ? 'active' : ''}" onclick="ReluxeFilters.togglePriceRange(${range.min}, ${range.max})">
          <span class="reluxe-checkbox ${isActive ? 'checked' : ''}"></span>
          <span class="reluxe-filter-label">${range.label}</span>
        </label>
      `;
    });

    html += `
          </div>
          <div class="reluxe-price-inputs">
            <input type="number" id="price-min" placeholder="Min $"
              value="${applied_filters.custom_min_price || ''}"
              onchange="ReluxeFilters.setPriceRange()">
            <span>to</span>
            <input type="number" id="price-max" placeholder="Max $"
              value="${applied_filters.custom_max_price || ''}"
              onchange="ReluxeFilters.setPriceRange()">
          </div>
        </div>
      </div>
    `;

    // Condition filter
    if (filters.conditions && filters.conditions.length > 0) {
      html += `
        <div class="reluxe-filter-group">
          <div class="reluxe-filter-title" onclick="ReluxeFilters.toggleGroup(this)">
            Condition <span class="reluxe-toggle">−</span>
          </div>
          <div class="reluxe-filter-options">
      `;
      filters.conditions.forEach(cond => {
        const isChecked = applied_filters.conditions && applied_filters.conditions.includes(cond.name);
        html += `
          <label class="reluxe-filter-option">
            <input type="checkbox" value="${cond.name}"
              ${isChecked ? 'checked' : ''}
              onchange="ReluxeFilters.toggleCondition('${cond.name.replace(/'/g, "\\'")}')">
            <span class="reluxe-checkbox ${isChecked ? 'checked' : ''}"></span>
            <span class="reluxe-filter-label">${cond.name}</span>
            <span class="reluxe-count">(${cond.count})</span>
          </label>
        `;
      });
      html += `
          </div>
        </div>
      `;
    }

    // Color filter - single column list with checkbox + circle + name
    if (filters.colors && filters.colors.length > 0) {
      html += `
        <div class="reluxe-filter-group">
          <div class="reluxe-filter-title" onclick="ReluxeFilters.toggleGroup(this)">
            Color <span class="reluxe-toggle">−</span>
          </div>
          <div class="reluxe-filter-options">
            <div class="reluxe-color-list">
      `;

      filters.colors.forEach(color => {
        const isChecked = applied_filters.colors && applied_filters.colors.includes(color.name);
        html += `
          <label class="reluxe-filter-option reluxe-color-option ${isChecked ? 'active' : ''}">
            <input type="checkbox" value="${color.name}" ${isChecked ? 'checked' : ''}
              onchange="ReluxeFilters.toggleColor('${color.name.replace(/'/g, "\\'")}')">
            <span class="reluxe-checkbox ${isChecked ? 'checked' : ''}"></span>
            ${renderColorSwatch(color.name)}
            <span class="reluxe-color-name">${color.name}</span>
            <span class="reluxe-color-count">(${color.count})</span>
          </label>
        `;
      });

      html += `
            </div>
          </div>
        </div>
      `;
    }

    // Material filter — collapsed by default, expanded if any material is active
    if (filters.materials && filters.materials.length > 0) {
      const materialActive = applied_filters.materials && applied_filters.materials.length > 0;
      html += `
        <div class="reluxe-filter-group">
          <div class="reluxe-filter-title" onclick="ReluxeFilters.toggleGroup(this)">
            Material <span class="reluxe-toggle">${materialActive ? '−' : '+'}</span>
          </div>
          <div class="reluxe-filter-options reluxe-scrollable" style="display:${materialActive ? 'block' : 'none'}">
      `;
      filters.materials.forEach(mat => {
        const isChecked = applied_filters.materials && applied_filters.materials.includes(mat.name);
        html += `
          <label class="reluxe-filter-option">
            <input type="checkbox" value="${mat.name}"
              ${isChecked ? 'checked' : ''}
              onchange="ReluxeFilters.toggleMaterial('${mat.name.replace(/'/g, "\\'")}')">
            <span class="reluxe-checkbox ${isChecked ? 'checked' : ''}"></span>
            <span class="reluxe-filter-label">${mat.name}</span>
            <span class="reluxe-count">(${mat.count})</span>
          </label>
        `;
      });
      html += `
          </div>
        </div>
      `;
    }

    // Size filter — only show when browsing categories with real sizes
    // (Apparel, Shoes, Watches, Jewelry/Ring) — not Bags, Accessories, Home
    const SIZE_CATEGORIES = ['Apparel', 'Shoes'];
    const SIZE_PRODUCT_TYPES = ['Ring'];
    const activeCategory = collectionFilters.category
      || (currentFilters.categories.length === 1 ? currentFilters.categories[0] : null);
    const activeProductType = collectionFilters.product_type
      || (currentFilters.product_types.length === 1 ? currentFilters.product_types[0] : null);
    const showSizeFilter = filters.sizes && filters.sizes.length > 0 && (
      SIZE_CATEGORIES.includes(activeCategory) ||
      SIZE_PRODUCT_TYPES.includes(activeProductType)
    );

    if (showSizeFilter) {
      const sizeActive = applied_filters.sizes && applied_filters.sizes.length > 0;
      const sizeOpen = sizeActive || currentFilters.size_group_open;
      const isUS = currentFilters.size_system === 'us';
      html += `
        <div class="reluxe-filter-group">
          <div class="reluxe-filter-title" onclick="ReluxeFilters.toggleSizeGroup()">
            Size <span class="reluxe-toggle">${sizeOpen ? '−' : '+'}</span>
          </div>
          <div class="reluxe-filter-options reluxe-scrollable" style="display:${sizeOpen ? 'block' : 'none'}">
            <div class="reluxe-size-toggle" style="display:flex;gap:0;margin-bottom:8px;border:1px solid #ccc;border-radius:4px;overflow:hidden;font-size:12px;">
              <button type="button" onclick="event.stopPropagation();ReluxeFilters.setSizeSystem('us')" style="flex:1;padding:4px 0;border:none;cursor:pointer;font-weight:${isUS ? '600' : '400'};background:${isUS ? '#222' : '#f5f5f5'};color:${isUS ? '#fff' : '#666'};">US</button>
              <button type="button" onclick="event.stopPropagation();ReluxeFilters.setSizeSystem('eu')" style="flex:1;padding:4px 0;border:none;cursor:pointer;font-weight:${!isUS ? '600' : '400'};background:${!isUS ? '#222' : '#f5f5f5'};color:${!isUS ? '#fff' : '#666'};">EU</button>
            </div>
      `;
      filters.sizes.forEach(size => {
        const isChecked = applied_filters.sizes && applied_filters.sizes.includes(size.name);
        html += `
          <label class="reluxe-filter-option">
            <input type="checkbox" value="${size.name}"
              ${isChecked ? 'checked' : ''}
              onchange="ReluxeFilters.toggleSize('${size.name.replace(/'/g, "\\'")}')">
            <span class="reluxe-checkbox ${isChecked ? 'checked' : ''}"></span>
            <span class="reluxe-filter-label">${size.name}</span>
            <span class="reluxe-count">(${size.count})</span>
          </label>
        `;
      });
      html += `
          </div>
        </div>
      `;
    }

    sidebar.innerHTML = html;
  }

  // Render products grid
  async function renderProducts() {
    const grid = document.getElementById('reluxe-product-grid');
    console.log('[ReluxeFilters] renderProducts - grid element:', grid);
    console.log('[ReluxeFilters] renderProducts - filterData:', filterData ? 'exists' : 'null');
    if (!grid || !filterData) {
      console.log('[ReluxeFilters] renderProducts - early return, grid or filterData missing');
      return;
    }

    const { products, total } = filterData;
    console.log('[ReluxeFilters] renderProducts - products count:', products.length, 'total:', total);
    console.log('[ReluxeFilters] renderProducts - grid innerHTML before:', grid.innerHTML.substring(0, 100));

    // Update product count
    const countEl = document.getElementById('reluxe-product-count');
    if (countEl) {
      countEl.textContent = `${total.toLocaleString()} products`;
    }

    // Update page title to show correct Algolia result count
    if (currentFilters.search) {
      document.title = `${total.toLocaleString()} results found for '${currentFilters.search}' | The Reluxe | Authentic Pre-Owned Luxury Bags`;
    }

    if (products.length === 0) {
      // Show different message for search pages vs filter pages
      if (currentFilters.search) {
        grid.innerHTML = `
          <div class="reluxe-no-results reluxe-no-results--search">
            <p class="reluxe-no-results__title">No results found for:</p>
            <p class="reluxe-no-results__query">"${currentFilters.search}"</p>
            <p class="reluxe-no-results__message">We couldn't find anything matching your search.</p>
            <p class="reluxe-no-results__suggestion">Try different keywords or browse our latest pieces.</p>
            <a href="/collections/new" class="reluxe-no-results__btn">Keep Shopping</a>
          </div>
        `;
      } else {
        grid.innerHTML = `
          <div class="reluxe-no-results">
            <p>No products match your filters.</p>
            <button onclick="ReluxeFilters.clearAll()">Clear all filters</button>
          </div>
        `;
      }
      return;
    }

    let html = '<ul class="reluxe-products-grid">';

    products.forEach(product => {
      const imageUrl = product.image || 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png';
      const image2Url = product.image2 || '';
      const isFavorited = isProductFavorited(product.handle);

      // DISABLED: Hover add-to-cart feature (uncomment to re-enable)
      // const addToCartBtn = `
      //   <button class="reluxe-product__add-to-cart" data-handle="${product.handle}" onclick="event.preventDefault(); ReluxeFilters.addToCartByHandle('${product.handle}', this)">
      //     Add to Cart
      //   </button>
      // `;
      const addToCartBtn = '';

      // Second image for hover effect (only if available)
      const hoverImageHtml = image2Url ? `
        <img class="reluxe-product__img reluxe-product__img--hover" src="${optimizeImageUrl(image2Url, 400)}" alt="${product.title}" loading="lazy" decoding="async" width="400" height="400">
      ` : '';

      // Add has-hover class if second image exists
      const hasHoverClass = image2Url ? 'reluxe-product--has-hover' : '';

      // Check if product is sold out (only use available field from Algolia)
      const isSoldOut = product.available === false;
      const soldOutClass = isSoldOut ? 'reluxe-product--soldout' : '';
      const soldOutOverlay = isSoldOut ? '<div class="reluxe-product__sold-out">SOLD OUT</div>' : '';

      // Badge display (Investment Piece, Great Value, Trending, Rare Find)
      const badgeHtml = (product.badges && product.badges.length > 0)
        ? `<div class="reluxe-product__badge" style="position: absolute; top: 8px; left: 8px; background: rgba(0, 0, 0, 0.83); color: #fff; font-size: 1.1rem; font-weight: 400; padding: 2px 6px; border-radius: 3px; text-transform: none; letter-spacing: 0.5px; z-index: 5;">${product.badges[0]}</div>`
        : '';

      html += `
        <li class="reluxe-product ${hasHoverClass} ${soldOutClass}" data-handle="${product.handle}">
          <a href="/products/${product.handle}" class="reluxe-product__link">
            <div class="reluxe-product__media">
              <img class="reluxe-product__img reluxe-product__img--primary" src="${optimizeImageUrl(imageUrl, 400)}" alt="${product.title}" loading="lazy" decoding="async" width="400" height="400" onerror="this.src='https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png'">
              ${hoverImageHtml}
              ${soldOutOverlay}
              ${badgeHtml}
              ${addToCartBtn}
              <button class="reluxe-product__favorite ${isFavorited ? 'reluxe-product__favorite--active' : ''}"
                      data-handle="${product.handle}"
                      onclick="event.preventDefault(); event.stopPropagation(); ReluxeFilters.toggleFavorite('${product.handle}', this)"
                      aria-label="${isFavorited ? 'Remove from favorites' : 'Add to favorites'}">
                <svg viewBox="0 0 24 24">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                </svg>
                <span class="reluxe-product__favorite-count">${getLikeCount(product.handle, product.category)}</span>
              </button>
            </div>
            <div class="reluxe-product__details">
              <span class="reluxe-product__vendor">${product.brand || ''}</span>
              <h3 class="reluxe-product__title">${truncateTitle(stripBrandFromTitle(product.title, product.brand), 10)}</h3>
              <div class="reluxe-product__price-row" style="display:flex;align-items:center;justify-content:space-between;gap:6px;width:100%;padding-right:8px;">
                <span class="reluxe-product__price">${formatPrice(product.price)}</span>
                <span class="reluxe-product__promo" style="display:inline-flex;align-items:center;background:#f0f0f0;color:#1a1a1a;padding:3px 6px;border-radius:3px;font-size:9px;font-weight:600;letter-spacing:0.4px;text-transform:uppercase;white-space:nowrap;">Save 10% code<span style="text-decoration:underline;margin-left:3px;">ICONS</span></span>
              </div>
            </div>
          </a>
        </li>
      `;
    });

    html += '</ul>';
    console.log('[ReluxeFilters] renderProducts - setting innerHTML, html length:', html.length);
    grid.innerHTML = html;
    console.log('[ReluxeFilters] renderProducts - grid innerHTML after:', grid.innerHTML.substring(0, 100));

    // Algolia Insights: track clicks on product cards
    grid.querySelectorAll('.reluxe-product__link').forEach((link) => {
      link.addEventListener('click', function(e) {
        const productEl = this.closest('.reluxe-product');
        if (!productEl) return;
        const handle = productEl.dataset.handle;
        // Find objectID and position from current results
        const product = products.find(p => p.handle === handle);
        if (!product) return;
        const objectID = product.sku || product.objectID;
        const position = currentHits.indexOf(objectID) + 1;  // 1-based position

        // Save search context to sessionStorage for product page tracking
        try {
          sessionStorage.setItem('_algolia_search_ctx', JSON.stringify({
            queryID: currentQueryID,
            objectID: objectID,
            position: position,
            index: ALGOLIA_INDEX_NAME,
            price: product.price || 0,
            timestamp: Date.now()
          }));
        } catch (e) {}

        if (currentQueryID && position > 0) {
          // Click after search (linked to query)
          sendAlgoliaEvent('click', {
            eventType: 'click',
            eventName: 'Product Clicked',
            index: ALGOLIA_INDEX_NAME,
            queryID: currentQueryID,
            objectIDs: [objectID],
            positions: [position]
          });
        } else {
          // Click without search context (browsing collection)
          sendAlgoliaEvent('click', {
            eventType: 'click',
            eventName: 'Product Clicked',
            index: ALGOLIA_INDEX_NAME,
            objectIDs: [objectID]
          });
        }
      });
    });

    // Pre-fetch variant IDs and load favorites
    const handles = products.map(p => p.handle);
    prefetchVariantIds(handles);
    fetchLikeCounts(handles);
  }

  // Render pagination
  function renderPagination() {
    const pagination = document.getElementById('reluxe-pagination');
    if (!pagination || !filterData) return;

    const { total, page, total_pages } = filterData;

    if (total_pages <= 1) {
      pagination.innerHTML = '';
      return;
    }

    let html = '<div class="reluxe-pagination">';

    // Left chevron - go back 1 page (only show if not on first page)
    if (page > 1) {
      html += `<span class="reluxe-pagination__arrow" onclick="ReluxeFilters.setPage(${page - 1})"><i class="chevron-arrow-left"></i></span>`;
    }

    // Show 5 page numbers centered around current page
    let startPage, endPage;
    if (total_pages <= 5) {
      startPage = 1;
      endPage = total_pages;
    } else if (page <= 3) {
      startPage = 1;
      endPage = 5;
    } else if (page >= total_pages - 2) {
      startPage = total_pages - 4;
      endPage = total_pages;
    } else {
      startPage = page - 2;
      endPage = page + 2;
    }

    for (let i = startPage; i <= endPage; i++) {
      const activeClass = i === page ? 'reluxe-pagination__num--active' : '';
      html += `<span class="reluxe-pagination__num ${activeClass}" onclick="ReluxeFilters.setPage(${i})">${i}</span>`;
    }

    // Right chevron - go forward 1 page (only show if not on last page)
    if (page < total_pages) {
      html += `<span class="reluxe-pagination__arrow" onclick="ReluxeFilters.setPage(${page + 1})"><i class="chevron-arrow-right"></i></span>`;
    }

    html += '</div>';
    pagination.innerHTML = html;
  }

  // UI Helper functions
  function showLoading() {
    const grid = document.getElementById('reluxe-product-grid');
    if (grid && !grid.querySelector('.reluxe-loading')) {
      const loader = document.createElement('div');
      loader.className = 'reluxe-loading';
      loader.innerHTML = '<div class="reluxe-spinner"></div>';
      grid.insertBefore(loader, grid.firstChild);
    }
  }

  function hideLoading() {
    const loader = document.querySelector('.reluxe-loading');
    if (loader) loader.remove();
  }

  function showError(message) {
    const grid = document.getElementById('reluxe-product-grid');
    if (grid) {
      grid.innerHTML = `<div class="reluxe-error">${message}</div>`;
    }
  }

  // Customer auth functions
  async function checkReluxeAuth() {
    try {
      const response = await fetch(`${API_BASE}/api/auth/me`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.user && data.user.id) {
          reluxeUserId = `reluxe_${data.user.id}`;
          return reluxeUserId;
        }
      }
    } catch (e) {}
    return null;
  }

  function getCustomerId() {
    if (reluxeUserId) return reluxeUserId;
    if (window.ReluxeCustomerId) return window.ReluxeCustomerId;
    if (window.__st && window.__st.cid) return window.__st.cid;
    return null;
  }

  function isProductFavorited(handle) {
    return customerFavorites.includes(handle);
  }

  // Generate a consistent "random" number based on product handle
function getRandomLikeCount(handle, category = '') {
  let hash = 0;
  for (let i = 0; i < handle.length; i++) {
    hash = ((hash << 5) - hash) + handle.charCodeAt(i);
    hash = hash & hash;
  }

  // Normalize to 0-1, then square to skew towards lower values
  const normalized = Math.abs(hash % 1000) / 1000;
  const skewed = normalized * normalized;
  const cat = (category || '').toLowerCase();

  // Category-based ranges (squared distribution = most items get lower values):
  // - Home/shoes/watches: limited range 3-25
  // - Bags/wallets/jewelry: higher range 8-120
  // - Everything else: standard range 3-60
  if (cat === 'home' || cat === 'shoes' || cat === 'watches') {
    return Math.floor(skewed * 22) + 3;
  } else if (cat === 'bags' || cat === 'wallets' || cat === 'jewelry') {
    return Math.floor(skewed * 112) + 8;
  } else {
    return Math.floor(skewed * 57) + 3;
  }
}

  function getLikeCount(handle, category = '') {
    const actualCount = likesCache[handle] || 0;
    // Always add real likes to the fake baseline number
    return actualCount + getRandomLikeCount(handle, category);
  }

  async function loadCustomerFavorites() {
    const customerId = getCustomerId();
    if (!customerId) return;

    try {
      const response = await fetch(`${API_BASE}/api/customer/${customerId}/favorites`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        customerFavorites = data.favorites || [];
        favoritesLoaded = true;

        customerFavorites.forEach(handle => {
          const btn = document.querySelector(`[data-handle="${handle}"] .reluxe-product__favorite`);
          if (btn) {
            btn.classList.add('reluxe-product__favorite--active');
          }
        });
      }
    } catch (e) {}
  }

  async function fetchLikeCounts(handles) {
    if (!handles || handles.length === 0) return;

    try {
      const response = await fetch(`${API_BASE}/api/likes?handles=${handles.join(',')}`);
      if (response.ok) {
        const data = await response.json();
        Object.assign(likesCache, data.likes);

        handles.forEach(handle => {
          const countEl = document.querySelector(`[data-handle="${handle}"] .reluxe-product__favorite-count`);
          if (countEl) {
            countEl.textContent = getLikeCount(handle);
          }
        });
      }
    } catch (e) {}

    loadCustomerFavorites();
  }

  async function toggleFavoriteOnServer(handle, isAdding) {
    const customerId = getCustomerId();
    if (!customerId) return { success: false };

    const method = isAdding ? 'POST' : 'DELETE';
    const url = `${API_BASE}/api/customer/${customerId}/favorites/${handle}`;

    try {
      const response = await fetch(url, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      const data = await response.json();
      if (response.ok && data.success) {
        likesCache[handle] = data.likes;
        return { success: true, likes: data.likes, action: data.action };
      }
    } catch (e) {}
    return { success: false };
  }

  function showSignInModal() {
    localStorage.setItem('reluxe_return_url', window.location.pathname + window.location.search);

    const modal = document.createElement('div');
    modal.className = 'reluxe-signin-modal';
    modal.innerHTML = `
      <div class="reluxe-signin-modal__backdrop" onclick="ReluxeFilters.closeSignInModal()"></div>
      <div class="reluxe-signin-modal__content">
        <button class="reluxe-signin-modal__close" onclick="ReluxeFilters.closeSignInModal()">×</button>
        <h3>Save Your Favorites</h3>
        <p>Enter your email to sign in or create an account. We'll send you a secure link.</p>
        <form class="reluxe-signin-modal__form" onsubmit="ReluxeFilters.submitMagicLink(event)">
          <input type="email" class="reluxe-signin-modal__input" placeholder="Enter your email" required id="reluxe-signin-email">
          <button type="submit" class="reluxe-signin-modal__btn reluxe-signin-modal__btn--primary">
            <span class="reluxe-signin-modal__btn-text">Continue</span>
            <span class="reluxe-signin-modal__btn-loading" style="display:none;">Sending...</span>
          </button>
        </form>
        <p class="reluxe-signin-modal__hint" style="font-size:12px;color:#999;margin-top:8px;">No password needed - we'll email you a sign-in link</p>
        <div class="reluxe-signin-modal__success" style="display:none;text-align:center;padding:12px 0;color:#22c55e;">✓ Check your email for a sign-in link!</div>
        <div class="reluxe-signin-modal__error" style="display:none;color:#e74c3c;font-size:13px;margin-top:8px;"></div>
      </div>
    `;
    document.body.appendChild(modal);
    setTimeout(() => {
      const input = document.getElementById('reluxe-signin-email');
      if (input) input.focus();
    }, 100);
  }

  function closeSignInModal() {
    const modal = document.querySelector('.reluxe-signin-modal');
    if (modal) modal.remove();
  }

  async function submitMagicLink(event) {
    event.preventDefault();
    const form = event.target;
    const email = document.getElementById('reluxe-signin-email').value.trim();
    if (!email) return;

    const btnText = form.querySelector('.reluxe-signin-modal__btn-text');
    const btnLoading = form.querySelector('.reluxe-signin-modal__btn-loading');
    const submitBtn = form.querySelector('button[type="submit"]');
    const successEl = document.querySelector('.reluxe-signin-modal__success');
    const errorEl = document.querySelector('.reluxe-signin-modal__error');
    const returnUrl = localStorage.getItem('reluxe_return_url') || '/';

    btnText.style.display = 'none';
    btnLoading.style.display = 'inline';
    submitBtn.disabled = true;
    errorEl.style.display = 'none';

    try {
      const response = await fetch(`${API_BASE}/api/auth/request-magic-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, return_url: returnUrl }),
        credentials: 'include'
      });

      const data = await response.json();

      if (data.success) {
        form.style.display = 'none';
        document.querySelector('.reluxe-signin-modal__hint').style.display = 'none';
        successEl.style.display = 'block';
      } else {
        errorEl.textContent = data.error || 'Something went wrong. Please try again.';
        errorEl.style.display = 'block';
        btnText.style.display = 'inline';
        btnLoading.style.display = 'none';
        submitBtn.disabled = false;
      }
    } catch (e) {
      errorEl.textContent = 'Connection error. Please try again.';
      errorEl.style.display = 'block';
      btnText.style.display = 'inline';
      btnLoading.style.display = 'none';
      submitBtn.disabled = false;
    }
  }

  // Cart functions
  async function getVariantId(handle) {
    if (variantCache[handle]) return variantCache[handle];

    try {
      const response = await fetch(`/products/${handle}.js`);
      if (response.ok) {
        const data = await response.json();
        const variant = data.variants.find(v => v.available) || data.variants[0];
        if (variant) {
          variantCache[handle] = variant.id;
          return variant.id;
        }
      }
    } catch (e) {}
    return null;
  }

  function prefetchVariantIds(handles) {
    const uncached = handles.filter(h => !variantCache[h]);
    uncached.forEach(handle => {
      fetch(`/products/${handle}.js`)
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data) {
            const variant = data.variants.find(v => v.available) || data.variants[0];
            if (variant) {
              variantCache[handle] = variant.id;
              // Update displayed price with Shopify's exact price in the active currency
              // (variant.price is already converted by Shopify server-side, in cents)
              const priceEl = document.querySelector(`[data-handle="${handle}"] .reluxe-product__price`);
              if (priceEl) priceEl.textContent = formatShopifyPrice(variant.price);
            }
          }
        })
        .catch(() => {});
    });
  }

  async function addToCartByHandle(handle, button) {
    const originalText = button.textContent;
    button.textContent = 'Adding...';
    button.disabled = true;

    try {
      const variantId = await getVariantId(handle);
      if (!variantId) throw new Error('Could not get variant');

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      });

      if (response.ok) {
        // Algolia Insights: track add-to-cart conversion
        const product = filterData?.products?.find(p => p.handle === handle);
        if (product) {
          const objectID = product.sku || handle;
          const eventPayload = {
            eventType: 'conversion',
            eventSubtype: 'addToCart',
            eventName: 'Product Added To Cart',
            index: ALGOLIA_INDEX_NAME,
            objectIDs: [objectID],
            objectData: [{
              price: product.price || 0,
              quantity: 1,
              ...(currentQueryID ? { queryID: currentQueryID } : {})
            }],
            currency: 'USD'
          };
          sendAlgoliaEvent('conversion', eventPayload);
        }

        button.textContent = 'Added!';
        setTimeout(() => {
          button.textContent = originalText;
          button.disabled = false;
        }, 2000);
      } else {
        throw new Error('Failed to add');
      }
    } catch (e) {
      button.textContent = 'Error';
      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
      }, 2000);
    }
  }

  // Public API
  window.ReluxeFilters = {
    init: function() {
      console.time('[ReluxeFilters] Total init time');

      // Initialize Algolia
      if (!initAlgolia()) {
        showError('Search engine failed to load. Please refresh.');
        return;
      }

      // Start auth check in background
      checkReluxeAuth();

      const detected = detectCollection();
      currentFilters.collection = detected.collection || 'all';
      currentFilters.search = detected.search || '';

      // Store collection-based filters
      collectionFilters.brand = detected.brand;
      collectionFilters.category = detected.category;
      collectionFilters.categories = detected.categories || null;  // Array of categories for multi-category collections (e.g., apparel)
      collectionFilters.product_type = detected.product_type;
      collectionFilters.product_types = detected.product_types || null;  // Array of product types for special collections
      collectionFilters.department = detected.department;
      collectionFilters.saleTag = detected.saleTag || false;
      collectionFilters.vdayTag = detected.vdayTag || false;  // Valentine's Day 2026 collection
      collectionFilters.mothersDayTag = detected.mothersDayTag || false;  // Mother's Day 2026 collection
      collectionFilters.editorsPickTag = detected.editorsPickTag || false;  // Editor's Picks
      collectionFilters.iconsTag = detected.iconsTag || false;  // Icons curated collection
      collectionFilters.prettyInPinkTag = detected.prettyInPinkTag || false;  // Pretty in Pink
      collectionFilters.newArrivals = detected.newArrivals || false;  // New arrivals filter
      collectionFilters.minTimestamp = detected.minTimestamp || null;  // Timestamp for new arrivals
      collectionFilters.search = detected.isSearch ? null : (detected.search || null);  // Store collection search (not user search)
      collectionFilters.titleRequired = detected.titleRequired || null;  // Word that must be in title (e.g., "belt")
      collectionFilters.restrictToTitle = detected.restrictToTitle || false;  // Only search in title field

      // If titleRequired is set, use it as the search query to filter by title
      if (detected.titleRequired && !currentFilters.search) {
        currentFilters.search = detected.titleRequired;
        collectionFilters.search = detected.titleRequired;
      }

      console.log('[ReluxeFilters] Collection filters set:', JSON.stringify(collectionFilters, null, 2));

      fetchFilters();
    },

    toggleBrand: function(brand) {
      const idx = currentFilters.brands.indexOf(brand);
      if (idx === -1) {
        currentFilters.brands.push(brand);
      } else {
        currentFilters.brands.splice(idx, 1);
      }
      currentFilters.page = 0;
      fetchFilters();
    },

    toggleCategory: function(category) {
      const idx = currentFilters.categories.indexOf(category);
      if (idx === -1) {
        currentFilters.categories.push(category);
      } else {
        currentFilters.categories.splice(idx, 1);
      }
      currentFilters.page = 0;
      fetchFilters();
    },

    toggleDepartment: function(department) {
      // Single-select: clicking the same department clears it, clicking different one switches
      if (currentFilters.department === department) {
        currentFilters.department = '';  // Clear if already selected
      } else {
        currentFilters.department = department;  // Set new department
      }
      currentFilters.page = 0;
      fetchFilters();
    },

    toggleProductType: function(type) {
      const idx = currentFilters.product_types.indexOf(type);
      if (idx === -1) {
        currentFilters.product_types.push(type);
      } else {
        currentFilters.product_types.splice(idx, 1);
      }
      currentFilters.page = 0;
      fetchFilters();
    },

    toggleColor: function(color) {
      const idx = currentFilters.colors.indexOf(color);
      if (idx === -1) {
        currentFilters.colors.push(color);
      } else {
        currentFilters.colors.splice(idx, 1);
      }
      currentFilters.page = 0;
      fetchFilters();
    },

    toggleCondition: function(condition) {
      const idx = currentFilters.conditions.indexOf(condition);
      if (idx === -1) {
        currentFilters.conditions.push(condition);
      } else {
        currentFilters.conditions.splice(idx, 1);
      }
      currentFilters.page = 0;
      fetchFilters();
    },

    toggleMaterial: function(material) {
      const idx = currentFilters.materials.indexOf(material);
      if (idx === -1) {
        currentFilters.materials.push(material);
      } else {
        currentFilters.materials.splice(idx, 1);
      }
      currentFilters.page = 0;
      fetchFilters();
    },

    toggleSize: function(size) {
      const idx = currentFilters.sizes.indexOf(size);
      if (idx === -1) {
        currentFilters.sizes.push(size);
      } else {
        currentFilters.sizes.splice(idx, 1);
      }
      currentFilters.page = 0;
      fetchFilters();
    },

    setSizeSystem: function(system) {
      if (currentFilters.size_system === system) return;
      currentFilters.size_system = system;
      currentFilters.sizes = [];  // Clear selections when switching systems
      currentFilters.size_group_open = true;  // Keep group expanded after toggle
      currentFilters.page = 0;
      fetchFilters();
    },

    toggleSizeGroup: function() {
      currentFilters.size_group_open = !currentFilters.size_group_open;
      const titles = document.querySelectorAll('.reluxe-filter-title');
      titles.forEach(el => {
        if (el.textContent.trim().startsWith('Size')) {
          const options = el.nextElementSibling;
          const toggle = el.querySelector('.reluxe-toggle');
          if (currentFilters.size_group_open) {
            options.style.display = 'block';
            if (toggle) toggle.textContent = '−';
          } else {
            options.style.display = 'none';
            if (toggle) toggle.textContent = '+';
          }
        }
      });
    },

    togglePriceRange: function(min, max) {
      // Multi-select price ranges - toggle on/off
      const idx = currentFilters.price_ranges.findIndex(r => r.min === min && r.max === max);
      if (idx === -1) {
        currentFilters.price_ranges.push({ min, max });
      } else {
        currentFilters.price_ranges.splice(idx, 1);
      }
      currentFilters.page = 0;
      fetchFilters();
    },

    setPage: function(page) {
      currentFilters.page = page - 1;  // Convert to 0-indexed for Algolia
      fetchFilters();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    setSort: function(sort) {
      currentFilters.sort = sort;
      currentFilters.page = 0;
      fetchFilters();
    },

    clearAll: function() {
      currentFilters.brands = [];
      currentFilters.categories = [];
      currentFilters.product_types = [];
      currentFilters.colors = [];
      currentFilters.conditions = [];
      currentFilters.materials = [];
      currentFilters.sizes = [];
      currentFilters.size_group_open = false;
      currentFilters.department = '';
      currentFilters.price_ranges = [];
      currentFilters.custom_min_price = null;
      currentFilters.custom_max_price = null;
      currentFilters.page = 0;
      fetchFilters();
    },

    toggleGroup: function(el) {
      const options = el.nextElementSibling;
      const toggle = el.querySelector('.reluxe-toggle');
      if (options.style.display === 'none') {
        options.style.display = 'block';
        toggle.textContent = '−';
      } else {
        options.style.display = 'none';
        toggle.textContent = '+';
      }
    },

    filterBrandList: function(query) {
      const brands = document.querySelectorAll('.reluxe-brand-list .reluxe-filter-option');
      const q = query.toLowerCase();
      brands.forEach(brand => {
        const name = brand.getAttribute('data-brand') || '';
        brand.style.display = name.includes(q) ? '' : 'none';
      });
    },

    toggleMobileFilters: function() {
      const sidebar = document.getElementById('reluxe-filter-sidebar');
      if (sidebar) {
        sidebar.classList.toggle('reluxe-sidebar-open');
        renderMobileToggle();
      }
    },

    setPriceRange: function() {
      const minInput = document.getElementById('price-min');
      const maxInput = document.getElementById('price-max');

      const minVal = minInput && minInput.value ? parseFloat(minInput.value) : null;
      const maxVal = maxInput && maxInput.value ? parseFloat(maxInput.value) : null;

      currentFilters.custom_min_price = minVal;
      currentFilters.custom_max_price = maxVal;
      currentFilters.page = 0;
      fetchFilters();
    },

    toggleFavorite: async function(handle, button) {
      const customerId = getCustomerId();
      if (!customerId) {
        showSignInModal();
        return;
      }

      const isCurrentlyFavorited = button.classList.contains('reluxe-product__favorite--active');
      const isAdding = !isCurrentlyFavorited;

      // Optimistic update
      button.classList.toggle('reluxe-product__favorite--active');
      const countEl = button.querySelector('.reluxe-product__favorite-count');
      let currentCount = parseInt(countEl?.textContent) || 0;
      if (isAdding) {
        currentCount++;
        customerFavorites.push(handle);
      } else {
        currentCount = Math.max(0, currentCount - 1);
        customerFavorites = customerFavorites.filter(h => h !== handle);
      }
      if (countEl) countEl.textContent = currentCount > 0 ? currentCount : '';

      // Server update
      const result = await toggleFavoriteOnServer(handle, isAdding);
      if (!result.success) {
        // Revert on failure
        button.classList.toggle('reluxe-product__favorite--active');
        if (countEl) countEl.textContent = (currentCount + (isAdding ? -1 : 1)) || '';
      }
    },

    addToCartByHandle: addToCartByHandle,
    showSignInModal: showSignInModal,
    closeSignInModal: closeSignInModal,
    submitMagicLink: submitMagicLink
  };

  // Initialize custom sort dropdown
  function initSortDropdown() {
    const dropdown = document.getElementById('reluxe-sort-dropdown');
    if (!dropdown) return;

    const trigger = dropdown.querySelector('.reluxe-dropdown__trigger');
    const selectedText = dropdown.querySelector('.reluxe-dropdown__selected');
    const items = dropdown.querySelectorAll('.reluxe-dropdown__item');

    // Toggle dropdown on trigger click
    trigger.addEventListener('click', (e) => {
      e.stopPropagation();
      dropdown.classList.toggle('reluxe-dropdown--open');
    });

    // Handle item selection
    items.forEach(item => {
      item.addEventListener('click', () => {
        const value = item.dataset.value;
        const text = item.textContent;

        // Update selected text
        selectedText.textContent = text;

        // Update active state
        items.forEach(i => i.classList.remove('reluxe-dropdown__item--active'));
        item.classList.add('reluxe-dropdown__item--active');

        // Close dropdown
        dropdown.classList.remove('reluxe-dropdown--open');

        // Trigger sort change
        ReluxeFilters.setSort(value);
      });
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!dropdown.contains(e.target)) {
        dropdown.classList.remove('reluxe-dropdown--open');
      }
    });

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        dropdown.classList.remove('reluxe-dropdown--open');
      }
    });
  }

  // Auto-initialize
  async function initFilter() {
    console.log('[ReluxeFilters] initFilter starting...');
    console.log('[ReluxeFilters] Current path:', window.location.pathname);
    console.log('[ReluxeFilters] Algolia available:', typeof algoliasearch !== 'undefined');

    // Initialize custom dropdown
    initSortDropdown();

    const path = window.location.pathname;
    if (path.includes('/collections/') || path === '/search' || path.startsWith('/search')) {
      console.log('[ReluxeFilters] Path matches, calling init...');
      try {
        ReluxeFilters.init();
      } catch (e) {
        console.error('[ReluxeFilters] Init error:', e);
        showError('Error initializing: ' + e.message);
      }
    } else {
      console.log('[ReluxeFilters] Path does not match, skipping init');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFilter);
  } else {
    initFilter();
  }

  /* Collapsible collection hero description */
  function initDescCollapse() {
    const desc = document.querySelector('.collection-hero__description.rte');
    if (!desc || desc.scrollHeight <= 60) return;
    desc.classList.add('reluxe-desc-collapsed');
    const btn = document.createElement('button');
    btn.className = 'reluxe-desc-toggle';
    btn.textContent = 'Read More';
    btn.addEventListener('click', function() {
      const collapsed = desc.classList.toggle('reluxe-desc-collapsed');
      desc.classList.toggle('reluxe-desc-expanded');
      btn.textContent = collapsed ? 'Read More' : 'Read Less';
    });
    desc.parentNode.insertBefore(btn, desc.nextSibling);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDescCollapse);
  } else {
    initDescCollapse();
  }

})();